// =============================================================================
// Admin Order Detail Page — Shopify-style layout
// Left column: fulfillment card, payment card, timeline
// Right column: customer info, routing/provider, notes
// =============================================================================

import { useState, useEffect, useRef, useCallback, type FormEvent, type KeyboardEvent, type ReactNode } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardHeader,
  Badge,
  Button,
  Input,
  Select,
  Spinner,
  Modal,
  ModalFooter,
  DropdownMenu,
  DropdownTrigger,
  DropdownContent,
  DropdownItem,
  DesignPartsSection,
  useConfirm,
  type ConfirmOptions,
} from '@tkpod/ui';
import type { DesignPartInfo } from '@tkpod/ui';
import { useApiClient, ApiClientError } from '@tkpod/api-client';
import {
  formatMoneyDto,
  RefundMode,
  RefundReason,
  type ListOrderRefundsResponse,
  type CreateOrderRefundResponse,
} from '@tkpod/shared';
import { ProviderFulfillmentCard } from '../components/order/ProviderFulfillmentCard';
import { AttachDesignCard } from '../components/order/AttachDesignCard';
import { EventTimeline } from '../components/order/EventTimeline';
import { SettlementBadge } from '../components/order/SettlementBadge';
import { SendToProviderModal, type InkteoFulfillmentChoice, type ProductionTypeChoice } from '../components/order/SendToProviderModal';
import {
  RevisionTimeline,
  type RevisionEntry,
} from '../components/order/RevisionTimeline';
import { useAuth } from '@/hooks/use-auth';
import {
  getTransitionLists,
  TRANSITION_LABELS,
  getDangerConfirmConfig,
  STATUS_LABELS,
} from '../components/order/orderStatusConstants';
import {
  getProviderTransition,
  canProviderReject,
  isOrderTerminal as isProviderTerminal,
  isEditable,
  hasPermission,
  ORDER_STATUS_MERCHANT_VARIANT,
  getMerchantOrderStatusLabel,
  isMerchantMaskedDanger,
  checkoutFulfillmentDisplay,
  orderItemDisplayName,
  orderItemStoreListingTitle,
} from '@tkpod/shared';

interface OrderItem {
  id: string;
  title: string;
  /** Catalog product a PRODUCT line is built from. Null for custom products. */
  catalogTitle?: string | null;
  variantTitle: string | null;
  sku: string | null;
  quantity: number;
  cancelledQty: number;
  unitPrice: string;
  totalPrice: string;
  currency: string;
  uptId?: string | null;
  uptName: string | null;
  uptType: string | null;
  presetName: string | null;
  blankVariantId?: string | null;
  variantSnapshot?: {
    options: Array<{ type: string; value: string; hex?: string }>;
    sku: string;
    supplierSku?: string | null;
    weightOz?: number | null;
    cost?: string;
  } | null;
  weightOz?: number | null;
  productOptions?: Array<{ name: string; value: string }> | null;
  proofStatus: string;
  providerId?: string | null;
  providerName?: string | null;
  warehouseId?: string | null;
  warehouseName?: string | null;
  warehouseCode?: string | null;
  routingSource?: string | null;
  designId?: string | null;
  designRefId?: string | null;
  partKey?: string | null;
  /** Stable public preview PNG URL carried on the Shopify line item. */
  designPreviewUrlStable?: string | null;
  /** Stable public 300-DPI export PNG URL carried on the Shopify line item. */
  designExportUrlStable?: string | null;
  designPreviewUrl?: string | null;
  /** PRODUCT-context line preview: ordered variant's hero composite. */
  variantPreviewUrl?: string | null;
  exportId?: string | null;
  exportStatus?: string | null;
  exportOutputUrl?: string | null;
  exportPreviewUrl?: string | null;
  sheetIndex?: number | null;
  designContext?: string | null;
  /** NONE means this product does not use the designer. */
  designerMode?: string | null;
  optionSubmissionId?: string | null;
  customization?: {
    selections: { label: string; value: string }[];
    uploads: {
      token?: string | null;
      label?: string | null;
      fileName: string | null;
      mimeType?: string | null;
      sizeBytes?: number | null;
      widthPx?: number | null;
      heightPx?: number | null;
      scanStatus?: string | null;
      versionNo: number | null;
      thumbUrl: string | null;
      previewUrl: string | null;
      downloadUrl: string | null;
    }[];
  } | null;
  designParts?: DesignPartInfo[];
  /** Order-time fulfillment snapshot — never changed by later product edits. */
  fulfillmentProvider?: 'TK_POD' | 'SELF';
  fulfillmentMethod?: 'AUTO' | 'MANUAL';
  /** Whether this line has entered production (any provider submission). */
  inProduction?: boolean;
  /**
   * Server-computed post-order edit verdict for the ADMIN surface. Mirrors the
   * SAVE endpoint exactly: TK lines are editable until production; SELF lines
   * are passive (read-only) for the admin.
   */
  editPermission?: {
    canEdit: boolean;
    reason: string | null;
    message: string | null;
  } | null;
}

interface ExportStatus {
  orderId: string;
  status: 'NONE' | 'PENDING' | 'READY' | 'FAILED';
  ready: boolean;
  failed: boolean;
  hasFiles: boolean;
  totals: {
    items: number;
    readyItems: number;
    pendingItems: number;
    failedItems: number;
  };
}

interface ProviderInfo {
  id: string;
  name: string;
}

interface WarehouseInfo {
  id: string;
  name: string;
  code: string;
}

interface OrderDetail {
  id: string;
  orderNumber: string;
  customerName: string | null;
  customerEmail: string | null;
  customerPhone: string | null;
  organizationId: string;
  organizationName: string | null;
  status: string;
  fulfillmentStatus: string;
  paymentStatus: string;
  source: string;
  subtotal: string;
  shippingTotal: string;
  taxTotal: string;
  discountTotal: string;
  total: string;
  currency: string;
  // Wholesale settlement (what TK collects from the merchant) — decoupled from
  // the Shopify customer paymentStatus above. Drives the settlement card.
  wholesaleProductionCost: string | null;
  wholesaleShippingCost: string | null;
  wholesaleTotalCost: string | null;
  shippingRateSnapshot?: {
    incomplete?: boolean;
    incompleteReason?: string | null;
    rateType?: string;
    basis?: string | null;
  } | null;
  wholesaleChargeStatus: string;
  wholesaleSettledVia: string | null;
  chargeHoldReason: string | null;
  lastChargeFailureCode: string | null;
  chargeAttemptCount: number;
  cardChargeAttempts: number;
  chargeHeldAt: string | null;
  nextChargeRetryAt: string | null;
  shippingAddress: Record<string, unknown> | null;
  billingAddress: Record<string, unknown> | null;
  customerOrderCount: number | null;
  shippingMethod: string | null;
  shopifyDeliveryMethod?: string | null;
  productionType?: 'REGULAR' | 'RUSH';
  productionTypeSource?: 'DEFAULT' | 'SHOPIFY_TAG' | 'ADMIN';
  resolvedProvider: ProviderInfo | null;
  resolvedWarehouse: WarehouseInfo | null;
  overrideProvider: ProviderInfo | null;
  overrideWarehouse: WarehouseInfo | null;
  overrideReason: string | null;
  overrideByUser: { id: string; name: string } | null;
  overrideAt: string | null;
  totalWeightOz: number;
  // Order-level fulfillment shape derived from line snapshots. ALL_SELF hides
  // TK production/shipping/cancel actions (the merchant fulfills everything).
  fulfillmentMix: 'ALL_TK_POD' | 'ALL_SELF' | 'MIXED';
  fulfillmentRequestedAt: string | null;
  store: { id: string; name: string; shopDomain: string | null } | null;
  selfClaim: {
    unclaimedSelfCount: number;
    claimedSelfCount: number;
    fulfillmentType: string | null;
    suggestedFulfillmentType?: 'SHIPPING' | 'PICKUP';
    claimedProvider: { id: string; name: string } | null;
    claimedAt: string | null;
    canSend: boolean;
    canClaim: boolean;
    canCorrectType?: boolean;
  };
  /** Merchant/store tags (Shopify-synced) — read-only in the admin. */
  tags: string[];
  /** Admin-only operational tags — editable, never synced to Shopify/portal. */
  internalTags?: string[];
  items: OrderItem[];
  notes: {
    id: string;
    body: string;
    userId: string | null;
    createdAt: string;
  }[];
  events: {
    id: string;
    type: string;
    data: Record<string, unknown> | null;
    userId: string | null;
    userName: string | null;
    createdAt: string;
  }[];
  shipments: {
    id: string;
    carrier: string | null;
    trackingNumber: string | null;
    trackingUrl: string | null;
    status: string;
    shippedAt: string | null;
    deliveredAt: string | null;
    fulfilledByProvider: { id: string; name: string } | null;
    items: { orderItemId: string; quantity: number }[];
    labelUrl: string | null;
    labelFormat: string | null;
    shopifyFulfillmentId: string | null;
    shopifySyncedAt: string | null;
    createdAt: string;
  }[];
  /**
   * EXTERNAL_API fulfillment authority summary (e.g. Inkteo). Null when the
   * order has no external provider submissions.
   */
  externalFulfillment: {
    provider: { id: string; name: string; slug: string };
    submissions: {
      id: string;
      orderItemId: string;
      status: string;
      lastError: string | null;
      trackingNumber: string | null;
      carrier: string | null;
      fulfillmentType?: string | null;
      submittedAt?: string | null;
      submittedVia?: string | null;
    }[];
    takenOver: boolean;
    canTakeover: boolean;
    canResubmit: boolean;
  } | null;
  orderCreatedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

const SHIP_TO_SOURCE_LABEL: Record<string, string> = {
  shipping: 'Customer shipping address',
  pickup_location: 'Shopify pickup location',
  billing: 'Billing address',
  provider_warehouse: 'Provider warehouse',
  tk_warehouse: 'TK warehouse',
};

/** Prior label files kept on TRACKING_UPDATED, not overwritten on the shipment row. */
function previousLabelLinks(
  events: OrderDetail['events'],
  shipment: { id: string; labelUrl: string | null },
): { id: string; url: string; tracking: string }[] {
  const seen = new Set<string>();
  const links: { id: string; url: string; tracking: string }[] = [];
  for (const event of events) {
    if (event.type !== 'TRACKING_UPDATED' || !event.data) continue;
    const previous = event.data.previous;
    if (!previous || typeof previous !== 'object') continue;
    const prev = previous as Record<string, unknown>;
    const url = typeof prev.labelUrl === 'string' ? prev.labelUrl : '';
    if (!url || url === shipment.labelUrl || seen.has(url)) continue;
    const shipmentId =
      typeof event.data.shipmentId === 'string' ? event.data.shipmentId : '';
    const replacedId =
      typeof event.data.replacedShipmentId === 'string'
        ? event.data.replacedShipmentId
        : '';
    if (shipmentId !== shipment.id && replacedId !== shipment.id) continue;
    seen.add(url);
    links.push({
      id: event.id,
      url,
      tracking: typeof prev.trackingNumber === 'string' ? prev.trackingNumber : '',
    });
  }
  return links;
}

const CARRIERS = [
  { value: '', label: 'Select Carrier' },
  { value: 'UPS', label: 'UPS' },
  { value: 'USPS', label: 'USPS' },
  { value: 'FedEx', label: 'FedEx' },
  { value: 'DHL', label: 'DHL' },
  { value: 'Other', label: 'Other' },
];

const CARRIER_TRACKING_URLS: Record<string, string> = {
  UPS: 'https://www.ups.com/track?tracknum={number}',
  USPS: 'https://tools.usps.com/go/TrackConfirmAction?tLabels={number}',
  FedEx: 'https://www.fedex.com/fedextrack/?trknbr={number}',
  DHL: 'https://www.dhl.com/us-en/home/tracking/tracking-parcel.html?submit=1&tracking-id={number}',
};

const TRACKING_NUMBER_REGEX = /^[A-Za-z0-9-]{5,50}$/;

function buildTrackingUrl(carrier: string, trackingNumber: string): string {
  const template = CARRIER_TRACKING_URLS[carrier];
  if (!template) return '';
  return template.replace('{number}', encodeURIComponent(trackingNumber));
}

function isValidTrackingNumber(value: string): boolean {
  return TRACKING_NUMBER_REGEX.test(value);
}

const PAYMENT_VARIANT: Record<string, 'success' | 'warning' | 'danger' | 'default'> = {
  PAID: 'success',
  PARTIALLY_PAID: 'warning',
  PENDING: 'warning',
  REFUNDED: 'danger',
  UNPAID: 'danger',
};

// Operator-facing explanations for the wholesale hold / failure reason codes.
const HOLD_REASON_LABEL: Record<string, string> = {
  NO_PAYMENT_METHOD: 'No card on file',
  CARD_DECLINED: 'Card declined',
  SCA_REQUIRED: 'Authentication required (SCA)',
  MANUAL_REVIEW: 'Manual review',
  DUNNING_EXHAUSTED: 'Dunning exhausted',
  WALLET_FROZEN: 'Wallet frozen',
  CARD_PENDING: 'Card processing',
  INSUFFICIENT_BALANCE: 'Insufficient wallet balance',
};

function settlementReasonLabel(
  reason: string | null,
  failureCode: string | null,
): string | null {
  const code = reason ?? failureCode;
  if (!code) return null;
  return HOLD_REASON_LABEL[code] ?? code;
}

function formatSettlementMoney(value: string | null, currency: string): string {
  if (value == null) return '—';
  const num = Number.parseFloat(value);
  if (!Number.isFinite(num)) return `${value} ${currency}`;
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(num);
  } catch {
    return `${num.toFixed(2)} ${currency}`;
  }
}

export function AdminOrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { confirm, dialog: confirmDialog } = useConfirm();
  const isProviderUser = !!user?.providerId;
  const permissions =
    user?.permissions ?? user?.currentOrganization?.permissions ?? [];
  const canManageOrder = hasPermission(permissions, 'order:manage');
  const [feedbackMessage, setFeedbackMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  const [confirmAction, setConfirmAction] = useState<{
    label: string;
    description: string;
    variant: 'danger' | 'warning';
    onConfirm: () => void;
  } | null>(null);
  const [showProviderRejectModal, setShowProviderRejectModal] = useState(false);
  const [providerRejectAction, setProviderRejectAction] = useState<'REJECT' | 'PROBLEM'>('REJECT');
  const [providerRejectReason, setProviderRejectReason] = useState('');

  const [showShipTrackingModal, setShowShipTrackingModal] = useState(false);
  const [shipCarrier, setShipCarrier] = useState('');
  const [shipTrackingNumber, setShipTrackingNumber] = useState('');
  const [shipCustomUrl, setShipCustomUrl] = useState('');
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [sendFulfillmentType, setSendFulfillmentType] =
    useState<InkteoFulfillmentChoice>('SHIPPING');
  const [sendProductionType, setSendProductionType] =
    useState<ProductionTypeChoice>('REGULAR');
  const [addressEditOpen, setAddressEditOpen] = useState(false);
  const [addressForm, setAddressForm] = useState({
    name: '',
    address1: '',
    address2: '',
    city: '',
    province: '',
    zip: '',
    country: '',
    phone: '',
  });

  const [labelModal, setLabelModal] = useState<{
    shipmentId: string;
    replacing: boolean;
  } | null>(null);
  const [labelCarrier, setLabelCarrier] = useState('');
  const [labelCarrierOther, setLabelCarrierOther] = useState('');
  const [labelTrackingNumber, setLabelTrackingNumber] = useState('');
  const [labelCustomUrl, setLabelCustomUrl] = useState('');
  const [labelFile, setLabelFile] = useState<File | null>(null);

  const [restrictionToast, setRestrictionToast] = useState<string | null>(null);
  const restrictionTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => {
    if (restrictionTimer.current) clearTimeout(restrictionTimer.current);
  }, []);

  const feedbackTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => {
    if (feedbackTimer.current) clearTimeout(feedbackTimer.current);
  }, []);

  const showFeedback = useCallback((type: 'success' | 'error', text: string) => {
    if (feedbackTimer.current) clearTimeout(feedbackTimer.current);
    setFeedbackMessage({ type, text });
    if (type === 'success') {
      feedbackTimer.current = setTimeout(() => setFeedbackMessage(null), 4000);
    }
  }, []);

  const extractErrorMessage = (err: Error, fallback: string): string => {
    if (err instanceof ApiClientError && err.apiError?.details?.length) {
      return err.apiError.details.map((d) => d.message).join('. ');
    }
    return err.message || fallback;
  };

  const orderQuery = useQuery({
    queryKey: ['admin', 'orders', id],
    queryFn: () =>
      api.get<{ data: OrderDetail }>(`/api/v1/admin/orders/${id}`),
    enabled: !!id,
    refetchInterval: (query) => {
      const data = query.state.data?.data;
      const items = data?.items ?? [];
      // Legacy lane: an item still bound to a per-order Export row that has not
      // settled (historical gangsheet/DTF orders created before the DesignPart
      // lane). New apparel/gangsheet lines carry no exportId, so this alone no
      // longer covers them.
      const legacyPending = items.some(
        (i: OrderItem) => i.exportId && i.exportStatus !== 'READY' && i.exportStatus !== 'FAILED',
      );
      // DesignPart lane: the order is mid-export (apparel SIDE / gangsheet SHEET
      // parts still rendering). These statuses flip server-side to EXPORTED /
      // EXPORT_FAILED, so keep polling to surface the transition live.
      const exporting =
        data?.status === 'EXPORT_QUEUED' || data?.status === 'EXPORT_PROCESSING';
      return legacyPending || exporting ? 5000 : false;
    },
  });

  const hasDesignFiles =
    orderQuery.data?.data?.items?.some(
      (it: OrderItem) => (it.designParts?.length ?? 0) > 0,
    ) ?? false;
  const hasOptionFiles =
    orderQuery.data?.data?.items?.some(
      (it: OrderItem) => (it.customization?.uploads?.length ?? 0) > 0,
    ) ?? false;
  const hasDownloadableFiles = hasDesignFiles || hasOptionFiles;

  // Lightweight readiness poll dedicated to the smart download control. Stops
  // as soon as the bundle is READY or terminally FAILED.
  const exportStatusQuery = useQuery({
    queryKey: ['admin', 'orders', id, 'export-status'],
    queryFn: () =>
      api.get<{ data: ExportStatus }>(`/api/v1/admin/orders/${id}/export-status`),
    enabled: !!id && hasDownloadableFiles,
    refetchInterval: (query) => {
      const s = query.state.data?.data;
      // Stop polling on terminal states. NONE means no in-scope item produces
      // files for this scope (e.g. provider-scoped user with no own items) —
      // it never transitions, so polling would spin forever.
      return s && (s.ready || s.failed || s.status === 'NONE') ? false : 5000;
    },
  });


  const refreshOrder = (freshData?: { data: OrderDetail }) => {
    if (freshData) {
      queryClient.setQueryData(['admin', 'orders', id], freshData);
    }
    void queryClient.refetchQueries({ queryKey: ['admin', 'orders', id] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'orders', 'stats'] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'orders'] });
  };

  const openAddressEdit = useCallback(() => {
    const detail = orderQuery.data?.data;
    const shipping = (detail?.shippingAddress ?? {}) as Record<string, string>;
    const billing = (detail?.billingAddress ?? {}) as Record<string, string>;
    const src =
      shipping.street1 || shipping.address1 || shipping.city ? shipping : billing;
    setAddressForm({
      name: src.name ?? detail?.customerName ?? '',
      address1: src.street1 ?? src.address1 ?? '',
      address2: src.street2 ?? src.address2 ?? '',
      city: src.city ?? '',
      province: src.province ?? src.provinceCode ?? '',
      zip: src.zip ?? '',
      country: src.country ?? src.countryCode ?? '',
      phone: src.phone ?? detail?.customerPhone ?? '',
    });
    setAddressEditOpen(true);
  }, [orderQuery.data]);

  const addressMutation = useMutation({
    mutationFn: (payload: Record<string, string>) =>
      api.patch<{ data: OrderDetail }>(`/api/v1/admin/orders/${id}/address`, payload),
    onSuccess: (res) => {
      setAddressEditOpen(false);
      showFeedback('success', 'Shipping address updated.');
      refreshOrder(res);
    },
    onError: (err: Error) =>
      showFeedback('error', extractErrorMessage(err, 'Failed to update address.')),
  });

  const handleAddressSave = useCallback(() => {
    const payload: Record<string, string> = {};
    for (const [key, value] of Object.entries(addressForm)) {
      if (value.trim()) payload[key] = value.trim();
    }
    addressMutation.mutate(payload);
  }, [addressForm, addressMutation]);

  const statusMutation = useMutation({
    mutationFn: (status: string) =>
      api.patch<{ data: OrderDetail }>(`/api/v1/admin/orders/${id}/status`, { status }),
    onSuccess: (res) => { setConfirmAction(null); showFeedback('success', 'Status updated successfully.'); refreshOrder(res); },
    onError: (err: Error) => { setConfirmAction(null); showFeedback('error', extractErrorMessage(err, 'Failed to update status.')); },
  });

  const holdMutation = useMutation({
    mutationFn: () => api.post<{ data: OrderDetail }>(`/api/v1/admin/orders/${id}/hold`),
    onSuccess: (res) => { setConfirmAction(null); showFeedback('success', 'Order placed on hold.'); refreshOrder(res); },
    onError: (err: Error) => { setConfirmAction(null); showFeedback('error', extractErrorMessage(err, 'Failed to hold order.')); },
  });

  const releaseMutation = useMutation({
    mutationFn: () => api.post<{ data: OrderDetail }>(`/api/v1/admin/orders/${id}/release`),
    onSuccess: (res) => { showFeedback('success', 'Order released from hold.'); refreshOrder(res); },
    onError: (err: Error) => showFeedback('error', extractErrorMessage(err, 'Failed to release order.')),
  });

  const cancelMutation = useMutation({
    mutationFn: () => api.post<{ data: OrderDetail }>(`/api/v1/admin/orders/${id}/cancel`),
    onSuccess: (res) => { setConfirmAction(null); showFeedback('success', 'Order cancelled.'); refreshOrder(res); },
    onError: (err: Error) => { setConfirmAction(null); showFeedback('error', extractErrorMessage(err, 'Failed to cancel order.')); },
  });

  // Re-render all print files under a new revision. The API only allows this
  // from a terminal export state (EXPORTED / EXPORT_FAILED / FAILED).
  const regenerateExportsMutation = useMutation({
    mutationFn: () =>
      api.post<{ orderId: string; revision: number; requeued: number }>(
        `/api/v1/admin/orders/${id}/regenerate-exports`,
      ),
    onSuccess: (res) => {
      showFeedback(
        'success',
        `Regenerating ${res.requeued} print file(s) — revision ${res.revision}.`,
      );
      void queryClient.refetchQueries({ queryKey: ['admin', 'orders', id] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'orders', id, 'export-status'] });
    },
    onError: (err: Error) => showFeedback('error', extractErrorMessage(err, 'Failed to regenerate exports.')),
  });

  // Revision history (post-order design edits) for the RevisionTimeline.
  const revisionsQuery = useQuery({
    queryKey: ['admin', 'orders', id, 'revisions'],
    queryFn: () =>
      api.get<{ data: RevisionEntry[] }>(`/api/v1/admin/orders/${id}/revisions`),
    enabled: !!id,
  });

  // Open the designer to edit a TK line's design (post-order revision flow).
  // The admin is typically not a member of the order's org, so the API mints a
  // cross-org `edit-order` launch (ADMIN surface, tenant-scoped to the order's
  // org, audited to the admin). The SAVE endpoint re-enforces the permission
  // matrix (SELF lines + in-production TK lines are denied).
  const editDesignMutation = useMutation({
    mutationFn: async (item: OrderItem) => {
      const res = await api.post<{
        data: { token: string; designerUrl: string; expiresAt: number };
      }>(`/api/v1/admin/orders/${id}/items/${item.id}/designer-launch-token`, {
        returnUrl: `${window.location.origin}/orders/${id}`,
      });
      return { res: res.data, item };
    },
    onSuccess: ({ res, item }) => {
      const url = new URL(`${res.designerUrl}/designer`);
      url.searchParams.set('context', 'edit-order');
      url.searchParams.set('token', res.token);
      url.searchParams.set('orderId', id!);
      url.searchParams.set('itemId', item.id);
      if (item.uptId) url.searchParams.set('uptId', item.uptId);
      url.searchParams.set('returnUrl', `${window.location.origin}/orders/${id}`);
      window.location.href = url.toString();
    },
    onError: (err: Error) =>
      showFeedback('error', extractErrorMessage(err, 'Failed to open the designer.')),
  });

  // Wholesale settlement: re-attempt the wallet→card charge. Safe to run
  // repeatedly — the API serializes via an atomic CHARGING claim.
  const retryChargeMutation = useMutation({
    mutationFn: () =>
      api.post<unknown>(`/api/v1/admin/orders/${id}/charge`, {
        reason: 'Admin manual retry from order detail',
      }),
    onSuccess: () => { showFeedback('success', 'Charge retried — refreshing settlement.'); refreshOrder(); },
    onError: (err: Error) => showFeedback('error', extractErrorMessage(err, 'Retry failed.')),
  });

  // Cancel an order held for an unresolved wholesale payment. Releases any
  // reserved wallet hold and notifies the merchant. Only valid while ON_HOLD.
  const cancelHoldMutation = useMutation({
    mutationFn: () =>
      api.post<unknown>(`/api/v1/admin/orders/${id}/cancel-hold`, {
        reason: 'Admin manual cancel from order detail',
      }),
    onSuccess: () => { setConfirmAction(null); showFeedback('success', 'Held order cancelled.'); refreshOrder(); },
    onError: (err: Error) => { setConfirmAction(null); showFeedback('error', extractErrorMessage(err, 'Cancel failed.')); },
  });

  const providerActionMutation = useMutation({
    mutationFn: (payload: { action: string; reason?: string }) =>
      api.patch<{ data: OrderDetail }>(`/api/v1/admin/orders/${id}/provider-action`, payload),
    onSuccess: (res) => {
      setShowProviderRejectModal(false);
      setProviderRejectReason('');
      showFeedback('success', 'Action completed.');
      refreshOrder(res);
    },
    onError: (err: Error) => showFeedback('error', extractErrorMessage(err, 'Action failed.')),
  });

  /**
   * Retry pushing a shipment to Shopify — when the initial push failed or
   * an admin edits tracking and needs to resync. Backend is idempotent via
   * Shipment.shopifyFulfillmentId.
   */
  const syncShopifyMutation = useMutation({
    mutationFn: (shipmentId: string) =>
      api.post<{ ok: true; shopifyFulfillmentId: string }>(
        `/api/v1/admin/orders/${id}/shipments/${shipmentId}/sync-shopify`,
      ),
    onSuccess: () => {
      showFeedback('success', 'Shipment synced to Shopify.');
      queryClient.invalidateQueries({ queryKey: ['admin', 'order', id] });
    },
    onError: (err: Error) =>
      showFeedback(
        'error',
        extractErrorMessage(err, 'Failed to sync shipment to Shopify.'),
      ),
  });

  // Take over fulfillment authority from an EXTERNAL_API provider (e.g.
  // Inkteo). After this the provider's callbacks are ignored and manual
  // tracking becomes possible.
  const takeoverMutation = useMutation({
    mutationFn: () =>
      api.post<{ data: { takenOver: number } }>(
        `/api/v1/admin/orders/${id}/fulfillment/takeover`,
        { reason: 'Admin takeover from order detail' },
      ),
    onSuccess: () => {
      setConfirmAction(null);
      showFeedback(
        'success',
        'Fulfillment taken over — provider callbacks will no longer apply.',
      );
      refreshOrder();
    },
    onError: (err: Error) => {
      setConfirmAction(null);
      showFeedback('error', extractErrorMessage(err, 'Takeover failed.'));
    },
  });

  // Reset FAILED submissions back to QUEUED after a provider failure. Under
  // the pull-only intake model the provider picks the order up on its next
  // poll of the Provider Pull API (≤15 min) — no outbound push is sent.
  // Primary recovery path; takeover + manual tracking is the fallback.
  const resubmitMutation = useMutation({
    mutationFn: () =>
      api.post<{ data: { resubmitted: number } }>(
        `/api/v1/admin/orders/${id}/fulfillment/resubmit`,
        {},
      ),
    onSuccess: () => {
      showFeedback(
        'success',
        'Requeued for the provider — it will be picked up on their next poll (within ~15 minutes).',
      );
      refreshOrder();
    },
    onError: (err: Error) =>
      showFeedback('error', extractErrorMessage(err, 'Requeue failed.')),
  });

  // Dispatch-delay grace window: is an outbound provider submit waiting, and
  // if so when will it fire? Powers the "Dispatching at <time>" banner and
  // the "Send now" button. Polls while a delayed job is pending.
  const dispatchQuery = useQuery({
    queryKey: ['admin', 'orders', id, 'dispatch'],
    queryFn: () =>
      api.get<{
        data: { pending: boolean; state: string | null; dispatchAt: string | null };
      }>(`/api/v1/admin/orders/${id}/dispatch`),
    enabled:
      !!id &&
      !isProviderUser &&
      !!orderQuery.data?.data?.externalFulfillment &&
      !orderQuery.data?.data?.externalFulfillment?.takenOver,
    refetchInterval: (query) =>
      query.state.data?.data?.state === 'delayed' ? 30_000 : false,
  });

  // "Send now": promote the delayed provider submit past the grace window.
  const dispatchNowMutation = useMutation({
    mutationFn: () =>
      api.post<{ data: { promoted: boolean } }>(
        `/api/v1/admin/orders/${id}/dispatch-now`,
        {},
      ),
    onSuccess: () => {
      showFeedback('success', 'Dispatch expedited — sending to the provider now.');
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'orders', id, 'dispatch'],
      });
      refreshOrder();
    },
    onError: (err: Error) =>
      showFeedback('error', extractErrorMessage(err, 'Failed to expedite dispatch.')),
  });

  const sendToProviderMutation = useMutation({
    mutationFn: (input: {
      fulfillmentType: InkteoFulfillmentChoice;
      productionType: 'REGULAR' | 'RUSH';
    }) =>
      api.post<{
        data: {
          claimed: boolean;
          alreadyClaimed: boolean;
          typeCorrected?: boolean;
          itemCount: number;
          fulfillmentType: string;
        };
      }>(`/api/v1/admin/orders/${id}/send-to-provider`, input),
    onSuccess: (res) => {
      setSendModalOpen(false);
      showFeedback(
        'success',
        res.data.typeCorrected
          ? 'Fulfillment type updated. The next provider payload will use the new type.'
          : res.data.alreadyClaimed
            ? 'Self-fulfilled lines were already with the provider.'
            : `Sent ${res.data.itemCount} self-fulfilled line(s) to the provider.`,
      );
      refreshOrder();
    },
  });

  const shipToPreviewQuery = useQuery({
    queryKey: ['admin', 'ship-to-preview', id, sendFulfillmentType],
    enabled: sendModalOpen && sendFulfillmentType === 'PICKUP' && !!id,
    queryFn: () =>
      api.get<{
        data: { source: string; summary: string; recipient: string };
      }>(
        `/api/v1/admin/orders/${id}/ship-to-preview?fulfillmentType=PICKUP`,
      ),
  });

  function openLabelModal(shipment: OrderDetail['shipments'][number]) {
    const known = CARRIERS.find(
      (option) =>
        option.value !== '' &&
        option.value.toUpperCase() === (shipment.carrier ?? '').toUpperCase(),
    );
    setLabelModal({
      shipmentId: shipment.id,
      replacing: Boolean(shipment.labelUrl),
    });
    setLabelCarrier(known?.value ?? (shipment.carrier ? 'Other' : ''));
    setLabelCarrierOther(known ? '' : (shipment.carrier ?? ''));
    setLabelTrackingNumber(shipment.trackingNumber ?? '');
    setLabelCustomUrl('');
    setLabelFile(null);
  }

  function closeLabelModal() {
    setLabelModal(null);
    setLabelFile(null);
  }

  // Attach a shipping label plus the carrier tracking that belongs with it.
  const uploadLabelMutation = useMutation({
    mutationFn: (payload: {
      shipmentId: string;
      file: File;
      carrier: string;
      trackingNumber: string;
      trackingUrl?: string;
    }) => {
      const formData = new FormData();
      formData.append('carrier', payload.carrier);
      formData.append('trackingNumber', payload.trackingNumber);
      if (payload.trackingUrl) formData.append('trackingUrl', payload.trackingUrl);
      formData.append('file', payload.file);
      return api.post<{ data: { labelUrl: string } }>(
        `/api/v1/admin/orders/${id}/shipments/${payload.shipmentId}/label`,
        formData,
      );
    },
    onSuccess: () => {
      closeLabelModal();
      showFeedback('success', 'Label and tracking saved.');
      refreshOrder();
    },
    onError: (err: Error) =>
      showFeedback('error', extractErrorMessage(err, 'Label upload failed.')),
  });

  const shipWithTrackingMutation = useMutation({
    mutationFn: async (payload: { carrier: string; trackingNumber: string; trackingUrl?: string }) => {
      const orderData = orderQuery.data?.data;
      if (!orderData) throw new Error('Order data not available');

      const activeShipments = (orderData.shipments ?? []).filter((s) => s.status !== 'CANCELLED');
      const shippedItemQtys = new Map<string, number>();
      for (const s of activeShipments) {
        for (const si of s.items) {
          shippedItemQtys.set(si.orderItemId, (shippedItemQtys.get(si.orderItemId) ?? 0) + si.quantity);
        }
      }

      const shipmentItems: Array<{ orderItemId: string; quantity: number }> = [];
      for (const item of orderData.items) {
        const effectiveQty = item.quantity - (item.cancelledQty ?? 0);
        const alreadyShipped = shippedItemQtys.get(item.id) ?? 0;
        const remaining = effectiveQty - alreadyShipped;
        if (remaining > 0) {
          shipmentItems.push({ orderItemId: item.id, quantity: remaining });
        }
      }

      if (shipmentItems.length === 0) throw new Error('No items to ship');

      const shipmentRes = await api.post<{ data: OrderDetail }>(
        `/api/v1/admin/orders/${id}/shipments`,
        {
          items: shipmentItems,
          carrier: payload.carrier,
          trackingNumber: payload.trackingNumber,
          trackingUrl: payload.trackingUrl || undefined,
        },
      );

      // createShipment auto-sets SHIPPED when all items are fulfilled;
      // only call updateStatus if the order hasn't already transitioned.
      const updatedOrder = shipmentRes?.data;
      if (updatedOrder?.status === 'SHIPPED') {
        return shipmentRes;
      }

      return api.patch<{ data: OrderDetail }>(`/api/v1/admin/orders/${id}/status`, {
        status: 'SHIPPED',
      });
    },
    onSuccess: (res) => {
      setShowShipTrackingModal(false);
      setShipCarrier('');
      setShipTrackingNumber('');
      setShipCustomUrl('');
      showFeedback('success', 'Shipment created and order marked as shipped.');
      refreshOrder(res);
    },
    onError: (err: Error) => showFeedback('error', extractErrorMessage(err, 'Failed to ship order.')),
  });

  const noteMutation = useMutation({
    mutationFn: (body: string) =>
      api.post<{ data: OrderDetail }>(`/api/v1/admin/orders/${id}/notes`, { body }),
    onSuccess: (res) => { showFeedback('success', 'Note added.'); refreshOrder(res); },
    onError: (err: Error) => showFeedback('error', extractErrorMessage(err, 'Failed to add note.')),
  });

  const addTagMutation = useMutation({
    mutationFn: (tag: string) =>
      api.post<{ data: OrderDetail }>(`/api/v1/admin/orders/${id}/tags`, { tag }),
    onSuccess: (res) => { showFeedback('success', 'Internal tag added.'); refreshOrder(res); },
    onError: (err: Error) => showFeedback('error', extractErrorMessage(err, 'Failed to add internal tag.')),
  });

  const removeTagMutation = useMutation({
    mutationFn: (tag: string) =>
      api.post<{ data: OrderDetail }>(`/api/v1/admin/orders/${id}/tags/remove`, { tag }),
    onSuccess: (res) => { showFeedback('success', 'Internal tag removed.'); refreshOrder(res); },
    onError: (err: Error) => showFeedback('error', extractErrorMessage(err, 'Failed to remove internal tag.')),
  });

  const [noteInput, setNoteInput] = useState('');
  const [timelineComment, setTimelineComment] = useState('');
  const [tagInput, setTagInput] = useState('');

  const order = orderQuery.data?.data;

  useEffect(() => {
    const sent = order?.selfClaim?.fulfillmentType;
    if (sent === 'PICKUP' || sent === 'SHIPPING') {
      setSendFulfillmentType(sent);
      return;
    }
    const suggested = order?.selfClaim?.suggestedFulfillmentType;
    if (suggested === 'PICKUP' || suggested === 'SHIPPING') {
      setSendFulfillmentType(suggested);
    }
  }, [
    order?.selfClaim?.fulfillmentType,
    order?.selfClaim?.suggestedFulfillmentType,
  ]);

  useEffect(() => {
    if (order?.productionType === 'RUSH' || order?.productionType === 'REGULAR') {
      setSendProductionType(order.productionType);
    }
  }, [order?.productionType]);

  if (orderQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner size="lg" />
      </div>
    );
  }

  if (orderQuery.isError) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-24 text-center">
        <p className="text-sm text-text-tertiary">Failed to load order.</p>
        <Button variant="outline" size="sm" onClick={() => void orderQuery.refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="py-24 text-center text-sm text-text-tertiary">
        Order not found.
      </div>
    );
  }

  const isMutating =
    statusMutation.isPending || holdMutation.isPending ||
    releaseMutation.isPending || cancelMutation.isPending ||
    providerActionMutation.isPending || shipWithTrackingMutation.isPending ||
    takeoverMutation.isPending;

  const addr = order.shippingAddress as Record<string, string> | null;
  const billingAddr = order.billingAddress as Record<string, string> | null;
  const totalItems = order.items.reduce((sum, i) => sum + i.quantity, 0);

  const {
    forwardTransitions,
    holdTransition,
    dangerTransitions,
    isOnHold,
    isTerminal,
    isAutoManaged,
    hasAnyActions,
  } = getTransitionLists(order.status);

  const canEditItems = isEditable(order.status, 'items');
  const canEditAddress = isEditable(order.status, 'address');

  // All-SELF orders are fulfilled entirely by the merchant: TK production,
  // shipping, and cancel actions are hidden (the server also blocks the
  // IN_PRODUCTION transition for these orders).
  const isAllSelf = order.fulfillmentMix === 'ALL_SELF';
  const isMixedFulfillment = order.fulfillmentMix === 'MIXED';
  const checkoutMethod = checkoutFulfillmentDisplay({
    shopifyDeliveryMethod: order.shopifyDeliveryMethod,
    shippingMethod: order.shippingMethod,
  });
  const sentFulfillmentType =
    order.selfClaim?.fulfillmentType === 'PICKUP' ||
    order.selfClaim?.fulfillmentType === 'SHIPPING'
      ? order.selfClaim.fulfillmentType
      : null;
  const isCorrectingType = Boolean(order.selfClaim?.canCorrectType);
  const isAwaitingApproval = order.status === 'AWAITING_FULFILLMENT_REQUEST';
  const selfItems = order.items.filter(
    (i) => i.fulfillmentProvider === 'SELF',
  );

  // Recovery surface (SH-238897): designer lines that ingested without a
  // design reference. designerMode NONE is a custom-options product — its
  // file is the option upload, not a designer design. PRODUCT lines resolve
  // their design at ingest and stay excluded.
  const unlinkedLines = order.items.filter(
    (i) =>
      !i.designId &&
      !i.designRefId &&
      !i.optionSubmissionId &&
      i.designerMode !== 'NONE' &&
      (i.designContext === 'STOREFRONT' || i.designContext == null),
  );
  const canAttachDesign =
    !isProviderUser && hasPermission(permissions, 'order:update');

  const providerTransition = getProviderTransition(order.status);
  const providerCanReject = canProviderReject(order.status);
  const providerIsTerminal = isProviderTerminal(order.status);
  const providerHasActions =
    !providerIsTerminal &&
    order.status !== 'SHIPPED' &&
    (!!providerTransition || providerCanReject);

  const activeShipments = (order.shipments ?? []).filter(
    (s) => s.status !== 'CANCELLED',
  );
  const hasShipmentWithTracking = activeShipments.some(
    (s) => s.trackingNumber && String(s.trackingNumber).trim() !== '',
  );
  const totalOrdered = (order.items ?? []).reduce(
    (sum, i) => sum + (i.quantity ?? 0),
    0,
  );
  const totalCancelled = (order.items ?? []).reduce(
    (sum, i) => sum + (i.cancelledQty ?? 0),
    0,
  );
  const totalShipped = activeShipments.reduce(
    (sum, s) =>
      sum +
      (s.items ?? []).reduce(
        (sq, si) => sq + (si.quantity ?? 0),
        0,
      ),
    0,
  );
  const remainingQty = totalOrdered - totalCancelled - totalShipped;
  const allItemsShipped = remainingQty <= 0;
  const canDeliver = hasShipmentWithTracking && allItemsShipped;
  const allowedForwardTransitions = forwardTransitions.filter((t) => {
    if (t === 'DELIVERED') return canDeliver;
    if (t === 'SHIPPED') return !allItemsShipped || !hasShipmentWithTracking;
    return true;
  });

  function openDangerConfirm(action: string, onConfirm: () => void) {
    const config = getDangerConfirmConfig(action);
    if (config) {
      setConfirmAction({ ...config, onConfirm });
    } else {
      onConfirm();
    }
  }

  return (
    <div className="space-y-6">
      {confirmDialog}
      <SendToProviderModal
        open={sendModalOpen}
        onClose={() => setSendModalOpen(false)}
        title={
          isCorrectingType
            ? 'Update fulfillment type'
            : isProviderUser
              ? 'Claim self-fulfilled lines'
              : 'Send to provider'
        }
        confirmLabel={
          isCorrectingType
            ? 'Update fulfillment type'
            : isProviderUser
              ? 'Claim'
              : 'Send to provider'
        }
        storeName={order.store?.name ?? 'Unknown store'}
        shopDomain={order.store?.shopDomain}
        selfLineCount={
          isCorrectingType
            ? (order.selfClaim?.claimedSelfCount ?? 0)
            : (order.selfClaim?.unclaimedSelfCount ?? 0)
        }
        mixedOrder={isMixedFulfillment}
        fulfillmentType={sendFulfillmentType}
        onFulfillmentTypeChange={setSendFulfillmentType}
        productionType={sendProductionType}
        onProductionTypeChange={setSendProductionType}
        productionNote={
          order.productionTypeSource === 'ADMIN'
            ? 'An operator already chose this production type. A later tag change will not replace it.'
            : order.tags.some((tag) => tag.trim().toLowerCase() === 'rush-production')
              ? 'Shopify tag rush-production selected Rush. You can change it before sending.'
              : 'No rush-production tag. Regular is selected. You can change it before sending.'
        }
        checkoutSuggested={order.selfClaim?.suggestedFulfillmentType ?? null}
        checkoutLabel={order.shippingMethod}
        correctingType={isCorrectingType}
        shipToPreview={
          sendFulfillmentType === 'PICKUP'
            ? {
                loading: shipToPreviewQuery.isLoading,
                sourceLabel: shipToPreviewQuery.data
                  ? (SHIP_TO_SOURCE_LABEL[shipToPreviewQuery.data.data.source] ??
                    shipToPreviewQuery.data.data.source)
                  : null,
                summary: shipToPreviewQuery.data?.data.summary ?? null,
                recipient: shipToPreviewQuery.data?.data.recipient ?? null,
                error: shipToPreviewQuery.isError
                  ? extractErrorMessage(
                      shipToPreviewQuery.error,
                      'Could not resolve a pickup ship-to.',
                    )
                  : null,
              }
            : null
        }
        onConfirm={() =>
          sendToProviderMutation.mutate({
            fulfillmentType: sendFulfillmentType,
            productionType:
              sendProductionType === 'DETECTED' ? 'REGULAR' : sendProductionType,
          })
        }
        loading={sendToProviderMutation.isPending}
        error={
          sendToProviderMutation.isError
            ? extractErrorMessage(
                sendToProviderMutation.error,
                'Failed to send to provider.',
              )
            : null
        }
      />
      {/* ─── Header (white bar like Shopify) ─── */}
      <div className="flex items-center justify-between rounded-lg bg-white px-4 py-3 sm:px-5">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/orders')}
            className="flex items-center justify-center rounded-md border border-border bg-surface p-2 text-text-secondary hover:bg-surface-secondary hover:text-text-primary transition-colors"
            title="Back to Orders"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-lg font-bold text-text-primary sm:text-xl">
                {order.orderNumber}
              </h1>
              {/* Merchant-facing label; hover reveals the internal status */}
              <Badge
                variant={ORDER_STATUS_MERCHANT_VARIANT[order.status] ?? 'default'}
                size="sm"
                title={`Internal status: ${STATUS_LABELS[order.status] ?? order.status.replace(/_/g, ' ')}`}
              >
                {getMerchantOrderStatusLabel(order.status)}
              </Badge>
                {getMerchantOrderStatusLabel(order.status) !==
                (STATUS_LABELS[order.status] ?? order.status.replace(/_/g, ' ')) &&
                (isMerchantMaskedDanger(order.status) ? (
                  // Internal failure hidden by the merchant remap — keep the
                  // ops alarm loud, not a muted caption.
                  <Badge variant="danger" size="sm">
                    {STATUS_LABELS[order.status] ?? order.status.replace(/_/g, ' ')}
                  </Badge>
                ) : (
                  <span className="text-[11px] uppercase tracking-wide text-text-tertiary">
                    {STATUS_LABELS[order.status] ?? order.status.replace(/_/g, ' ')}
                  </span>
                ))}
              {order.productionType === 'RUSH' && (
                <Badge variant="warning" size="sm" title="Production queue priority">
                  Rush production
                </Badge>
              )}
              <Badge variant={PAYMENT_VARIANT[order.paymentStatus] ?? 'default'} size="sm">
                {order.paymentStatus.replace(/_/g, ' ')}
              </Badge>
              <Badge
                variant={
                  order.fulfillmentStatus === 'FULFILLED' ? 'success'
                    : order.fulfillmentStatus === 'PARTIAL' ? 'info'
                      : 'warning'
                }
                size="sm"
              >
                {order.fulfillmentStatus === 'PARTIAL'
                  ? 'PARTIALLY FULFILLED'
                  : order.fulfillmentStatus.replace(/_/g, ' ')}
              </Badge>
              {isAllSelf && (
                <Badge variant="warning" size="sm">
                  SELF-FULFILL
                </Badge>
              )}
              {isMixedFulfillment && (
                <Badge variant="info" size="sm">
                  MIXED FULFILLMENT
                </Badge>
              )}
              <Badge
                variant={checkoutMethod.type === 'PICKUP' ? 'info' : 'default'}
                size="sm"
                title={
                  order.shopifyDeliveryMethod
                    ? `Shopify checkout: ${order.shopifyDeliveryMethod}`
                    : 'Checkout fulfillment method'
                }
              >
                {checkoutMethod.label}
              </Badge>
              {sentFulfillmentType &&
                sentFulfillmentType !== checkoutMethod.type && (
                  <Badge variant="warning" size="sm">
                    Sent as {sentFulfillmentType === 'PICKUP' ? 'Pickup' : 'Shipping'}
                  </Badge>
                )}
            </div>
            <p className="mt-0.5 text-[11px] text-text-tertiary">
              {new Date(order.createdAt).toLocaleDateString('en-US', {
                month: 'short', day: 'numeric', year: 'numeric',
              })} at {new Date(order.createdAt).toLocaleTimeString('en-US', {
                hour: 'numeric', minute: '2-digit',
              })} from {order.source}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <Button
            variant="outline"
            size="sm"
            onClick={() => void orderQuery.refetch()}
            loading={orderQuery.isFetching && !orderQuery.isLoading}
          >
            Refresh
          </Button>
          {(order.selfClaim?.canSend || order.selfClaim?.canCorrectType) &&
            hasPermission(permissions, 'order:update') && (
              <Button
                variant="primary"
                size="sm"
                disabled={isMutating || sendToProviderMutation.isPending}
                onClick={() => setSendModalOpen(true)}
              >
                {isCorrectingType
                  ? 'Update fulfillment type'
                  : isProviderUser
                    ? 'Claim'
                    : 'Send to provider'}
              </Button>
            )}
          {!isProviderUser && !isAllSelf && allowedForwardTransitions.length > 0 && (() => {
            const approveTarget = allowedForwardTransitions[0];
            return (
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  if (approveTarget === 'SHIPPED' && !hasShipmentWithTracking) {
                    setShowShipTrackingModal(true);
                  } else {
                    statusMutation.mutate(approveTarget);
                  }
                }}
                disabled={isMutating}
                loading={statusMutation.isPending}
              >
                {TRANSITION_LABELS[approveTarget] ?? approveTarget.replace(/_/g, ' ')}
              </Button>
            );
          })()}
          {!isProviderUser && !isAllSelf && !isTerminal && hasAnyActions ? (
            <DropdownMenu align="right">
              <DropdownTrigger>
                <span className="inline-flex items-center gap-1 rounded-md border border-border bg-surface px-2.5 py-1.5 text-xs font-medium text-text-secondary hover:bg-surface-secondary hover:text-text-primary transition-colors">
                  More
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </span>
              </DropdownTrigger>
              <DropdownContent minWidth="min-w-[240px]">
                {allowedForwardTransitions.map((t) => (
                  <DropdownItem
                    key={t}
                    onClick={() => {
                      if (t === 'SHIPPED' && !hasShipmentWithTracking) {
                        setShowShipTrackingModal(true);
                      } else {
                        statusMutation.mutate(t);
                      }
                    }}
                    disabled={isMutating}
                  >
                    {TRANSITION_LABELS[t] ?? t.replace(/_/g, ' ')}
                  </DropdownItem>
                ))}
                {isOnHold ? (
                  <DropdownItem onClick={() => releaseMutation.mutate()} disabled={isMutating}>
                    Release from Hold
                  </DropdownItem>
                ) : holdTransition ? (
                  <DropdownItem
                    onClick={() => openDangerConfirm('ON_HOLD', () => holdMutation.mutate())}
                    disabled={isMutating}
                  >
                    Put on Hold
                  </DropdownItem>
                ) : null}
                {dangerTransitions.map((t) => (
                  <DropdownItem
                    key={t}
                    danger
                    onClick={() => {
                      const run = t === 'CANCELLED'
                        ? () => cancelMutation.mutate()
                        : () => (t === 'CANCEL_REQUESTED' ? statusMutation.mutate('CANCEL_REQUESTED') : statusMutation.mutate(t));
                      openDangerConfirm(t, run);
                    }}
                    disabled={isMutating}
                  >
                    {TRANSITION_LABELS[t] ?? t.replace(/_/g, ' ')}
                  </DropdownItem>
                ))}
              </DropdownContent>
            </DropdownMenu>
          ) : !isAllSelf && providerHasActions ? (
            <DropdownMenu align="right">
              <DropdownTrigger>
                <span className="inline-flex items-center gap-1 rounded-md border border-border bg-surface px-2.5 py-1.5 text-xs font-medium text-text-secondary hover:bg-surface-secondary hover:text-text-primary transition-colors">
                  More
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </span>
              </DropdownTrigger>
              <DropdownContent minWidth="min-w-[240px]">
                {providerTransition && (
                  <DropdownItem
                    onClick={() => {
                      if (providerTransition.next === 'SHIPPED' && !hasShipmentWithTracking) {
                        setShowShipTrackingModal(true);
                      } else {
                        statusMutation.mutate(providerTransition.next);
                      }
                    }}
                    disabled={isMutating}
                  >
                    {providerTransition.label}
                  </DropdownItem>
                )}
                {providerCanReject && (
                  <>
                    <DropdownItem
                      danger
                      onClick={() => {
                        setProviderRejectAction('REJECT');
                        setProviderRejectReason('');
                        setShowProviderRejectModal(true);
                      }}
                      disabled={isMutating}
                    >
                      Reject Order
                    </DropdownItem>
                    <DropdownItem
                      onClick={() => {
                        setProviderRejectAction('PROBLEM');
                        setProviderRejectReason('');
                        setShowProviderRejectModal(true);
                      }}
                      disabled={isMutating}
                    >
                      Report Problem
                    </DropdownItem>
                  </>
                )}
              </DropdownContent>
            </DropdownMenu>
          ) : null}
        </div>
      </div>

      {/* Feedback Banner — Polaris-aligned */}
      {feedbackMessage && (
        <div
          className="flex items-center justify-between rounded-lg border px-3 py-2"
          style={feedbackMessage.type === 'success'
            ? { backgroundColor: 'var(--color-banner-success-bg)', borderColor: 'var(--color-banner-success-border)', color: 'var(--color-banner-success-text)' }
            : { backgroundColor: 'var(--color-banner-critical-bg)', borderColor: 'var(--color-banner-critical-border)', color: 'var(--color-banner-critical-text)' }
          }
        >
          <span className="text-xs font-medium">{feedbackMessage.text}</span>
          <button onClick={() => setFeedbackMessage(null)} className="ml-3 text-sm leading-none hover:opacity-70">
            ×
          </button>
        </div>
      )}

      {/* Restriction toast — shown on demand when user clicks a restricted action */}
      {restrictionToast && (
        <div className="flex items-center justify-between rounded-lg border px-4 py-3 text-sm animate-in fade-in slide-in-from-top-2 duration-200" style={{ backgroundColor: 'var(--color-banner-warning-bg)', borderColor: 'var(--color-banner-warning-border)', color: 'var(--color-banner-warning-text)' }}>
          <span>{restrictionToast}</span>
          <button
            type="button"
            onClick={() => { 
              if (restrictionTimer.current) clearTimeout(restrictionTimer.current); 
              setRestrictionToast(null); 
            }}
            className="ml-4 text-lg leading-none transition-opacity hover:opacity-70"
            style={{ color: 'var(--color-banner-warning-text)' }}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {/* Awaiting merchant approval banner (manual fulfillment hold) */}
      {isAwaitingApproval && (
        <div className="rounded-md border border-amber-500/20 bg-amber-500/10 px-4 py-3 text-sm text-amber-700 dark:text-amber-400">
          <strong>Awaiting approval</strong> — this order is held until the
          merchant clicks <strong>Request fulfillment</strong> in their Shopify
          admin. Routing and billing start automatically once approved.
        </div>
      )}

      {/* Self-fulfilled order banner — TK takes no production/shipping action */}
      {isAllSelf && (
        <div className="rounded-md border border-amber-500/20 bg-amber-500/10 px-4 py-3 text-sm text-amber-700 dark:text-amber-400">
          <strong>Self-fulfilled</strong> — the merchant is not billed.
          {order.selfClaim?.claimedSelfCount > 0 && order.selfClaim.claimedProvider ? (
            <>
              {' '}
              Production was handed to{' '}
              <strong>{order.selfClaim.claimedProvider.name}</strong>
              {order.selfClaim.fulfillmentType
                ? ` (${order.selfClaim.fulfillmentType.toLowerCase()})`
                : ''}
              .
            </>
          ) : (
            <>
              {' '}
              Print files remain available. An allowlisted provider can take
              these lines without a wholesale charge.
            </>
          )}
        </div>
      )}

      {/* Auto-managed export pipeline banner */}
      {isAutoManaged && !isAwaitingApproval && (
        <div className="flex items-center gap-2 rounded-md border border-blue-500/20 bg-blue-500/10 px-4 py-3 text-sm text-blue-700 dark:text-blue-400">
          <svg className="h-4 w-4 shrink-0 animate-spin" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          Export is being processed automatically. Status will update when complete.
        </div>
      )}

      {/* Edit restriction banner */}
      {!canEditAddress && !isTerminal && !isAutoManaged && (
        <div className="rounded-md border border-amber-500/20 bg-amber-500/10 px-4 py-3 text-sm text-amber-700 dark:text-amber-400">
          This order cannot be edited because it is in <strong>{order.status.replace(/_/g, ' ').toLowerCase()}</strong> status. Address and item changes are only allowed before production starts.
        </div>
      )}

      {/* ─── Two-column layout (Shopify-like: main + sidebar) ─── */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        {/* ═══ LEFT COLUMN ═══ */}
        <div className="space-y-4">
          {/* Recovery: lines that ingested without design linkage */}
          {unlinkedLines.length > 0 && (
            <AttachDesignCard
              orderId={order.id}
              organizationId={order.organizationId}
              lines={unlinkedLines.map((i) => ({
                id: i.id,
                title: i.title,
                variantTitle: i.variantTitle,
                quantity: i.quantity,
                sku: i.sku,
              }))}
              canAttach={canAttachDesign}
              onSuccess={() => refreshOrder()}
              onFeedback={showFeedback}
            />
          )}

          {/* Download / export actions — ZIP, summary PDF and regenerate in
              one responsive row. The summary PDF is ALWAYS offered (the
              endpoint self-heals a missing render); the ZIP slot reflects
              export readiness. */}
          {(() => {
            const xs = exportStatusQuery.data?.data;
            // No in-scope downloadable files → the ZIP/regenerate slots hide,
            // the summary PDF stays.
            const showZipSlot = hasDownloadableFiles && !(xs && xs.status === 'NONE');

            const downloadIcon = (
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
            );

            const summaryPdfLink = (
              <a
                href={`${import.meta.env.VITE_API_URL ?? ''}/api/v1/admin/orders/${order.id}/summary.pdf`}
                target="_blank"
                rel="noreferrer"
                className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-border bg-surface px-4 py-2.5 text-sm font-medium text-text-primary transition-colors hover:bg-surface-secondary"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                </svg>
                Order summary (PDF)
              </a>
            );

            const regenerateButton = hasDesignFiles && showZipSlot && (xs?.failed || xs?.ready) && (
              <button
                type="button"
                onClick={() => regenerateExportsMutation.mutate()}
                disabled={regenerateExportsMutation.isPending}
                className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-border bg-surface px-4 py-2.5 text-sm font-medium text-text-primary transition-colors hover:bg-surface-secondary disabled:cursor-not-allowed disabled:opacity-60"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992V4.356M19.5 14.652a8.25 8.25 0 01-15.038-2.158M4.5 9.348A8.25 8.25 0 0119.538 7.19M3 19.644V14.652h4.992" />
                </svg>
                {regenerateExportsMutation.isPending ? 'Regenerating…' : 'Regenerate exports'}
              </button>
            );

            let zipSlot: ReactNode = null;
            if (showZipSlot) {
              if (xs?.failed) {
                // The strict admin ZIP endpoint 409s on gaps; a partial
                // download is an explicit, confirmed operator decision
                // (?allowPartial=1) — never a silent default (SH-238897).
                zipSlot = (
                  <div className="flex flex-1 flex-col gap-1.5">
                    <div className="flex items-center justify-center gap-2 rounded-lg border border-danger/40 bg-danger/5 px-4 py-2.5 text-sm font-medium text-danger">
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                      </svg>
                      Print file export failed
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        setConfirmAction({
                          label: 'Download partial ZIP',
                          description:
                            'Some print files failed to render, so the ZIP will be missing those files. The manifest.json inside the archive lists every gap. Only continue if you intend to produce from an incomplete bundle.',
                          variant: 'warning',
                          onConfirm: () => {
                            window.open(
                              `${import.meta.env.VITE_API_URL ?? ''}/api/v1/admin/orders/${order.id}/export.zip?allowPartial=1`,
                              '_blank',
                              'noopener',
                            );
                            setConfirmAction(null);
                          },
                        })
                      }
                      className="text-xs font-medium text-text-secondary underline-offset-2 hover:text-text-primary hover:underline"
                    >
                      Download partial ZIP anyway…
                    </button>
                  </div>
                );
              } else if (xs?.ready) {
                zipSlot = (
                  <a
                    href={`${import.meta.env.VITE_API_URL ?? ''}/api/v1/admin/orders/${order.id}/export.zip`}
                    className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-border bg-surface px-4 py-2.5 text-sm font-medium text-text-primary transition-colors hover:bg-surface-secondary"
                  >
                    {downloadIcon}
                    Download all print files (ZIP)
                  </a>
                );
              } else {
                // PENDING / loading → preparing state (download disabled).
                zipSlot = (
                  <div
                    className="flex flex-1 cursor-not-allowed items-center justify-center gap-2 rounded-lg border border-border bg-surface-secondary px-4 py-2.5 text-sm font-medium text-text-secondary"
                    aria-busy="true"
                  >
                    <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth={4} />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Preparing print files…
                  </div>
                );
              }
            }

            return (
              <div className="flex flex-col gap-2 sm:flex-row">
                {zipSlot}
                {summaryPdfLink}
                {regenerateButton}
              </div>
            );
          })()}

          {/* Self-fulfilled items — read-only card, no TK fulfillment actions.
              The merchant prints and ships these lines themselves. */}
          {selfItems.length > 0 && (
            <Card>
              <CardHeader title="Self-fulfilled items" />
              <div className="px-4 pb-2 sm:px-5">
                <p className="text-xs text-text-secondary">
                  The merchant handles printing and shipping for these items.
                  They are not billed and are excluded from TK production.
                </p>
              </div>
              <div className="divide-y divide-border">
                {selfItems.map((item) => (
                  <div key={item.id} className="px-4 py-2.5 sm:px-5">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-text-primary">
                          {orderItemDisplayName(item)}
                        </p>
                        {orderItemStoreListingTitle(item) && (
                          <p className="truncate text-xs text-text-tertiary">
                            Store listing: {orderItemStoreListingTitle(item)}
                          </p>
                        )}
                        {item.variantTitle && (
                          <p className="truncate text-xs text-text-tertiary">
                            {item.variantTitle}
                          </p>
                        )}
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <Badge variant="warning" size="sm">
                          Self-fulfill
                        </Badge>
                        <span className="text-xs text-text-secondary">
                          × {item.quantity}
                        </span>
                      </div>
                    </div>
                    {/* Print files / mockups so the merchant can produce the
                        item themselves. Read-only: preview + 300 DPI download. */}
                    <DesignPartsSection
                      parts={item.designParts}
                      previewImageUrl={item.variantPreviewUrl}
                    />
                    {/* Edit design — passive for SELF lines (admin is view-only
                        per the permission matrix), so this renders disabled with
                        the reason. Kept for parity so the action is discoverable. */}
                    {item.editPermission && item.designId && (() => {
                      const perm = item.editPermission;
                      const canEdit = (perm?.canEdit ?? false) && !!item.uptId;
                      const isOpening =
                        editDesignMutation.isPending &&
                        editDesignMutation.variables?.id === item.id;
                      return (
                        <div className="mt-2">
                          <Button
                            variant={canEdit ? 'secondary' : 'outline'}
                            size="sm"
                            disabled={!canEdit || isOpening}
                            loading={isOpening}
                            title={!canEdit ? perm?.message ?? undefined : undefined}
                            onClick={() => {
                              if (canEdit) editDesignMutation.mutate(item);
                            }}
                          >
                            Edit design
                          </Button>
                          {!canEdit && perm?.message && (
                            <p className="mt-0.5 text-[10px] text-text-tertiary">
                              {perm.message}
                            </p>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Provider-grouped Fulfillment Cards — TK lines only. SELF lines
              are never routed/produced by TK and are listed above instead. */}
          {(() => {
            const tkItems = order.items.filter(
              (i) => i.fulfillmentProvider !== 'SELF',
            );
            const providerGroups = new Map<string, OrderItem[]>();
            for (const item of tkItems) {
              const key = item.providerId ?? '__unassigned__';
              if (!providerGroups.has(key)) providerGroups.set(key, []);
              providerGroups.get(key)!.push(item);
            }

            const sortedKeys = [...providerGroups.keys()].sort((a, b) => {
              if (a === '__unassigned__') return 1;
              if (b === '__unassigned__') return -1;
              return 0;
            });

            return sortedKeys.map((key) => {
              const groupItems = providerGroups.get(key)!;
              const firstItem = groupItems[0];
              const providerId = key === '__unassigned__' ? null : key;
              const providerName =
                key === '__unassigned__'
                  ? 'Unassigned'
                  : firstItem.providerName ?? 'Unknown Provider';

              return (
                <ProviderFulfillmentCard
                  key={key}
                  orderId={order.id}
                  status={order.status}
                  providerId={providerId}
                  providerName={providerName}
                  items={groupItems}
                  shipments={order.shipments ?? []}
                  createdAt={order.createdAt}
                  isProviderUser={isProviderUser}
                  canEditItems={canEditItems}
                  actionsDisabled={isAllSelf}
                  onFeedback={showFeedback}
                  onEditDesign={(itemId) => {
                    const it = order.items.find((i) => i.id === itemId);
                    if (it) editDesignMutation.mutate(it);
                  }}
                  editPendingItemId={
                    editDesignMutation.isPending
                      ? editDesignMutation.variables?.id ?? null
                      : null
                  }
                />
              );
            });
          })()}

          {/* Payment Card */}
          <Card>
            <CardHeader title="Payment" />
            <div className="px-4 pb-1.5 sm:px-5">
              <Badge variant={PAYMENT_VARIANT[order.paymentStatus] ?? 'default'} size="sm">
                {order.paymentStatus.replace(/_/g, ' ')}
              </Badge>
            </div>
            <div className="px-4 pb-4 pt-2 sm:px-5 sm:pb-5 space-y-1.5">
              <div className="flex items-center justify-between text-xs">
                <span className="text-text-secondary">Subtotal ({totalItems} item{totalItems !== 1 ? 's' : ''})</span>
                <span className="text-text-primary">${order.subtotal}</span>
              </div>
              {parseFloat(order.shippingTotal) > 0 && (
                <div className="flex items-center justify-between text-xs">
                  <span className="text-text-secondary">Shipping</span>
                  <span className="text-text-primary">${order.shippingTotal}</span>
                </div>
              )}
              {parseFloat(order.taxTotal) > 0 && (
                <div className="flex items-center justify-between text-xs">
                  <span className="text-text-secondary">Tax</span>
                  <span className="text-text-primary">${order.taxTotal}</span>
                </div>
              )}
              {parseFloat(order.discountTotal) > 0 && (
                <div className="flex items-center justify-between text-xs">
                  <span className="text-text-secondary">Discount</span>
                  <span className="text-text-primary">-${order.discountTotal}</span>
                </div>
              )}
              <div className="flex items-center justify-between text-xs font-bold border-t border-border pt-1.5 mt-1.5">
                <span className="text-text-primary">Total</span>
                <span className="text-text-primary">${order.total} {order.currency}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-text-secondary">Paid</span>
                <span className="text-text-primary">${order.total}</span>
              </div>
              {order.totalWeightOz > 0 && (
                <div className="flex items-center justify-between text-xs mt-1.5 pt-1.5 border-t border-border">
                  <span className="text-text-secondary">Total Weight</span>
                  <span className="text-text-primary">{order.totalWeightOz} oz</span>
                </div>
              )}
            </div>
          </Card>

          {/* Wholesale settlement Card — what TK collects from the merchant.
              Decoupled from the customer-facing Payment card above. */}
          {order.wholesaleChargeStatus &&
            order.wholesaleChargeStatus !== 'NONE' && (
              <Card>
                <CardHeader title="Wholesale settlement" />
                {(order.wholesaleChargeStatus === 'ON_HOLD' ||
                  order.wholesaleChargeStatus === 'FAILED') && (
                  <div className="px-4 sm:px-5">
                    <div
                      className="rounded-md border px-3 py-2 text-xs"
                      style={{
                        backgroundColor: 'var(--color-banner-critical-bg)',
                        borderColor: 'var(--color-banner-critical-border)',
                        color: 'var(--color-banner-critical-text)',
                      }}
                    >
                      This order will not enter production until the wholesale
                      charge is collected. Retry after the merchant tops up or
                      fixes their card.
                    </div>
                  </div>
                )}
                <div className="px-4 pb-4 pt-3 sm:px-5 sm:pb-5 space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-text-secondary">Status</span>
                    <SettlementBadge status={order.wholesaleChargeStatus} />
                  </div>
                  {settlementReasonLabel(
                    order.chargeHoldReason,
                    order.lastChargeFailureCode,
                  ) && (
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-text-secondary">Reason</span>
                      <span className="text-text-primary">
                        {settlementReasonLabel(
                          order.chargeHoldReason,
                          order.lastChargeFailureCode,
                        )}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-text-secondary">Amount</span>
                    <span className="text-text-primary">
                      {formatSettlementMoney(
                        order.wholesaleTotalCost,
                        order.currency,
                      )}
                    </span>
                  </div>
                  {order.shippingRateSnapshot?.incomplete ? (
                    <div
                      className="rounded-md border px-3 py-2 text-xs"
                      style={{
                        backgroundColor: 'var(--color-banner-warning-bg, var(--color-banner-critical-bg))',
                        borderColor: 'var(--color-banner-warning-border, var(--color-banner-critical-border))',
                        color: 'var(--color-banner-warning-text, var(--color-banner-critical-text))',
                      }}
                    >
                      Shipping was billed from the open-ended tier because{' '}
                      {order.shippingRateSnapshot.incompleteReason === 'MISSING_BASIS'
                        ? order.shippingRateSnapshot.rateType === 'ORDER_AMOUNT'
                          ? 'a billed line is missing its merchandise price.'
                          : 'a billed line is missing its catalog weight.'
                        : 'the package did not match a configured band.'}{' '}
                      Review the rate before settlement.
                    </div>
                  ) : null}
                  {order.wholesaleSettledVia && (
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-text-secondary">Settled via</span>
                      <span className="text-text-primary">
                        {order.wholesaleSettledVia}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-text-secondary">Attempts</span>
                    <span className="text-text-primary">
                      {order.chargeAttemptCount}
                      {order.cardChargeAttempts > 0
                        ? ` (${order.cardChargeAttempts} card)`
                        : ''}
                    </span>
                  </div>
                  {order.chargeHeldAt && (
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-text-secondary">Held since</span>
                      <span className="text-text-primary">
                        {new Date(order.chargeHeldAt).toLocaleString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                        })}
                      </span>
                    </div>
                  )}
                  {canManageOrder &&
                    (order.wholesaleChargeStatus === 'ON_HOLD' ||
                      order.wholesaleChargeStatus === 'FAILED' ||
                      order.wholesaleChargeStatus === 'PENDING') && (
                      <div className="flex justify-end gap-2 border-t border-border pt-2 mt-1.5">
                        {order.wholesaleChargeStatus === 'ON_HOLD' && (
                          <Button
                            variant="danger"
                            size="sm"
                            disabled={
                              retryChargeMutation.isPending ||
                              cancelHoldMutation.isPending
                            }
                            onClick={() =>
                              openDangerConfirm('CANCELLED', () =>
                                cancelHoldMutation.mutate(),
                              )
                            }
                          >
                            Cancel hold
                          </Button>
                        )}
                        <Button
                          variant="primary"
                          size="sm"
                          disabled={
                            retryChargeMutation.isPending ||
                            cancelHoldMutation.isPending
                          }
                          loading={retryChargeMutation.isPending}
                          onClick={() => retryChargeMutation.mutate()}
                        >
                          Retry charge
                        </Button>
                      </div>
                    )}
                  {canManageOrder &&
                    order.wholesaleChargeStatus === 'PAID' && (
                      <WholesaleRefundSection
                        orderId={order.id}
                        currency={order.currency}
                        settledVia={order.wholesaleSettledVia}
                        confirm={confirm}
                      />
                    )}
                </div>
              </Card>
            )}

          {/* External fulfillment authority (EXTERNAL_API providers, e.g. Inkteo) */}
          {order.externalFulfillment && (
            <Card>
              <CardHeader
                title={`External fulfillment — ${order.externalFulfillment.provider.name}`}
              />
              <div className="px-4 pb-4 sm:px-5 sm:pb-5 space-y-3">
                {order.externalFulfillment.takenOver ? (
                  <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 p-3">
                    <Badge variant="warning" size="sm">
                      Admin takeover
                    </Badge>
                    <p className="text-xs text-text-secondary">
                      Fulfillment authority was taken over by an admin. Provider
                      callbacks are recorded but no longer applied — ship
                      manually and enter tracking below.
                    </p>
                  </div>
                ) : (
                  <p className="text-xs text-text-secondary">
                    This order is fulfilled by{' '}
                    <span className="font-medium text-text-primary">
                      {order.externalFulfillment.provider.name}
                    </span>
                    . Tracking and labels arrive automatically via provider
                    callbacks.
                  </p>
                )}
                {/* Dispatch-delay grace window: pending delayed submit */}
                {dispatchQuery.data?.data?.state === 'delayed' &&
                  dispatchQuery.data.data.dispatchAt && (
                    <div className="flex items-center justify-between gap-2 rounded-md border border-info/40 bg-info/10 p-3">
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-text-primary">
                          Dispatching to {order.externalFulfillment.provider.name} at{' '}
                          {new Date(dispatchQuery.data.data.dispatchAt).toLocaleString()}
                        </p>
                        <p className="text-[11px] text-text-tertiary">
                          Cancellation grace window — cancel the order before this
                          time to keep it from reaching the provider.
                        </p>
                      </div>
                      {hasPermission(permissions, 'order:update') && !isProviderUser && (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={isMutating || dispatchNowMutation.isPending}
                          loading={dispatchNowMutation.isPending}
                          onClick={() => {
                            void (async () => {
                              const ok = await confirm({
                                title: 'Send to provider now',
                                description:
                                  'This skips the remaining cancellation grace window and sends the order to the provider immediately. This cannot be undone.',
                                confirmLabel: 'Send now',
                                tone: 'danger',
                              });
                              if (ok) dispatchNowMutation.mutate();
                            })();
                          }}
                        >
                          Send now
                        </Button>
                      )}
                    </div>
                  )}
                <div className="space-y-1.5">
                  {order.externalFulfillment.submissions.map((sub) => {
                    const item = order.items.find((i) => i.id === sub.orderItemId);
                    return (
                      <div
                        key={sub.id}
                        className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-xs font-medium text-text-primary">
                            {item ? orderItemDisplayName(item) : sub.orderItemId}
                          </p>
                          {sub.trackingNumber && (
                            <p className="text-[10px] text-text-tertiary">
                              {sub.carrier ? `${sub.carrier} · ` : ''}
                              {sub.trackingNumber}
                            </p>
                          )}
                          {sub.lastError && (
                            <p className="text-[10px] text-danger">
                              {sub.lastError}
                            </p>
                          )}
                        </div>
                        <Badge
                          size="sm"
                          variant={
                            sub.status === 'SHIPPED'
                              ? 'success'
                              : sub.status === 'FAILED'
                                ? 'danger'
                                : sub.status === 'ADMIN_OVERRIDDEN'
                                  ? 'warning'
                                  : sub.status === 'CANCELLED'
                                    ? 'default'
                                    : 'info'
                          }
                        >
                          {sub.status.replace(/_/g, ' ')}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
                {canManageOrder &&
                  !isProviderUser &&
                  order.externalFulfillment.canResubmit && (
                    <div className="flex items-center justify-between gap-2 border-t border-border pt-3">
                      <p className="text-[11px] text-text-tertiary">
                        Submission failed. Requeue it — the provider picks the
                        order up on its next poll of the pull API.
                      </p>
                      <Button
                        variant="primary"
                        size="sm"
                        disabled={isMutating || resubmitMutation.isPending}
                        loading={resubmitMutation.isPending}
                        onClick={() => resubmitMutation.mutate()}
                      >
                        Requeue for provider
                      </Button>
                    </div>
                  )}
                {canManageOrder &&
                  !isProviderUser &&
                  order.externalFulfillment.canTakeover && (
                    <div className="flex items-center justify-between gap-2 border-t border-border pt-3">
                      <p className="text-[11px] text-text-tertiary">
                        Provider cannot fulfill? Take over to ship manually and
                        enter tracking yourself.
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={isMutating}
                        onClick={() =>
                          setConfirmAction({
                            label: 'Take over fulfillment',
                            description:
                              `This stops ${order.externalFulfillment?.provider.name ?? 'the provider'} from controlling this order. ` +
                              'Future provider callbacks will be recorded as conflicts but not applied. ' +
                              'You will need to ship manually and enter tracking. This cannot be undone.',
                            variant: 'warning',
                            onConfirm: () => takeoverMutation.mutate(),
                          })
                        }
                      >
                        Take over fulfillment
                      </Button>
                    </div>
                  )}
              </div>
            </Card>
          )}

          {/* Shipments summary */}
          {order && (order.shipments ?? []).length > 0 && (
            <Card>
              <CardHeader title={`Shipments (${order.shipments.length})`} />
              <div className="px-4 pb-4 sm:px-5 sm:pb-5 space-y-3">
                {order.shipments.map((shipment) => (
                  <div
                    key={shipment.id}
                    className="rounded-md border border-border p-3 space-y-2"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge
                          variant={
                            shipment.status === 'DELIVERED'
                              ? 'success'
                              : shipment.status === 'CANCELLED'
                                ? 'danger'
                                : shipment.status === 'IN_TRANSIT'
                                  ? 'info'
                                  : shipment.status === 'LABEL_CREATED'
                                    ? 'warning'
                                    : 'default'
                          }
                          size="sm"
                        >
                          {shipment.status.replace(/_/g, ' ')}
                        </Badge>
                        {shipment.carrier && (
                          <span className="text-xs font-medium text-text-primary">
                            {shipment.carrier}
                          </span>
                        )}
                      </div>
                      {shipment.fulfilledByProvider && (
                        <span className="text-[10px] text-text-tertiary">
                          by {shipment.fulfilledByProvider.name}
                        </span>
                      )}
                    </div>
                    {shipment.trackingNumber && (
                      <div className="text-xs text-text-secondary">
                        Tracking:{' '}
                        {shipment.trackingUrl ? (
                          <a
                            href={shipment.trackingUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-brand-primary hover:underline"
                          >
                            {shipment.trackingNumber}
                          </a>
                        ) : (
                          shipment.trackingNumber
                        )}
                      </div>
                    )}
                    <div className="text-[10px] text-text-tertiary space-y-0.5">
                      {shipment.items.map((si) => {
                        const item = order.items.find(
                          (i) => i.id === si.orderItemId,
                        );
                        return (
                          <div key={si.orderItemId}>
                            {item ? orderItemDisplayName(item) : si.orderItemId} x{si.quantity}
                          </div>
                        );
                      })}
                    </div>
                    {shipment.shippedAt && (
                      <div className="text-[10px] text-text-tertiary">
                        Shipped:{' '}
                        {new Date(shipment.shippedAt).toLocaleDateString(
                          'en-US',
                          {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          },
                        )}
                      </div>
                    )}
                    {/* Shipping label — view / upload / replace */}
                    {(shipment.labelUrl ||
                      (canManageOrder &&
                        !isProviderUser &&
                        shipment.status !== 'CANCELLED')) && (
                      <div className="flex items-center justify-between gap-2 pt-1 border-t border-border">
                        {shipment.labelUrl ? (
                          <div className="flex flex-col gap-0.5">
                            <a
                              href={shipment.labelUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs font-medium text-brand-primary hover:underline"
                            >
                              View label{shipment.labelFormat ? ` (${shipment.labelFormat})` : ''}
                            </a>
                            {previousLabelLinks(order.events, shipment).map((prev) => (
                              <a
                                key={prev.id}
                                href={prev.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[11px] text-text-tertiary hover:underline"
                              >
                                Previous label{prev.tracking ? ` (${prev.tracking})` : ''}
                              </a>
                            ))}
                          </div>
                        ) : (
                          <span className="text-[11px] text-text-tertiary">
                            No label attached
                          </span>
                        )}
                        {canManageOrder &&
                          !isProviderUser &&
                          shipment.status !== 'CANCELLED' && (
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={uploadLabelMutation.isPending}
                              onClick={() => openLabelModal(shipment)}
                            >
                              {shipment.labelUrl ? 'Replace label' : 'Upload label'}
                            </Button>
                          )}
                      </div>
                    )}
                    {order.source === 'SHOPIFY' && (
                      <div className="flex items-center justify-between gap-2 pt-1 border-t border-border">
                        {shipment.shopifyFulfillmentId ? (
                          <Badge variant="success" size="sm">
                            ✓ Synced to Shopify
                            {shipment.shopifySyncedAt && (
                              <span className="ml-1 opacity-75">
                                {new Date(
                                  shipment.shopifySyncedAt,
                                ).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                })}
                              </span>
                            )}
                          </Badge>
                        ) : (
                          <Badge variant="warning" size="sm">
                            Not synced to Shopify
                          </Badge>
                        )}
                        {!shipment.shopifyFulfillmentId &&
                          shipment.status !== 'CANCELLED' && (
                            <Button
                              variant="secondary"
                              size="sm"
                              disabled={syncShopifyMutation.isPending}
                              onClick={async () => {
                                // Pre-handoff shipment: pushing creates the
                                // Shopify fulfillment and emails the customer
                                // "shipped" — make staff confirm the package
                                // is actually moving before announcing it.
                                if (shipment.status === 'LABEL_CREATED') {
                                  const ok = await confirm({
                                    title: 'Push shipment to Shopify?',
                                    description:
                                      'This shipment only has a label — the carrier handoff has not been confirmed by the provider yet. Pushing now will mark the order fulfilled in Shopify and send the customer a shipping notification. Verify the tracking is active with the carrier first.',
                                    confirmLabel: 'Push to Shopify',
                                    tone: 'danger',
                                  });
                                  if (!ok) return;
                                }
                                syncShopifyMutation.mutate(shipment.id);
                              }}
                            >
                              {syncShopifyMutation.isPending
                                ? 'Syncing…'
                                : shipment.status === 'LABEL_CREATED'
                                  ? 'Push to Shopify'
                                  : 'Retry Shopify sync'}
                            </Button>
                          )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Revision history — post-order design edits, shown directly above
              the order-event timeline. Rendered whenever the order has any
              design-editable line or at least one captured revision. */}
          {(() => {
            const revisions = revisionsQuery.data?.data ?? [];
            const hasEditableLine = order.items.some((it) => it.editPermission);
            if (revisions.length === 0 && !hasEditableLine) {
              return null;
            }
            const apiBase = import.meta.env.VITE_API_URL ?? '';
            return (
              <Card>
                <CardHeader title="Revision history" />
                <div className="px-4 pb-4 sm:px-5 sm:pb-5">
                  <RevisionTimeline
                    revisions={revisions}
                    orderId={order.id}
                    apiBase={apiBase}
                  />
                </div>
              </Card>
            );
          })()}

          {/* Timeline — comment box + events (Shopify-style) */}
          <Card>
            <CardHeader title="Timeline" />
            <div className="px-4 pb-4 sm:px-5 sm:pb-5 space-y-4">
              <form
                onSubmit={(e: FormEvent) => {
                  e.preventDefault();
                  const val = timelineComment.trim();
                  if (!val) return;
                  noteMutation.mutate(val);
                  setTimelineComment('');
                }}
                className="flex gap-2"
              >
                <textarea
                  placeholder="Leave a comment..."
                  value={timelineComment}
                  onChange={(e) => setTimelineComment(e.target.value)}
                  rows={2}
                  className="flex-1 rounded-md border border-border bg-surface px-3 py-2 text-xs text-text-primary placeholder:text-text-tertiary focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary resize-none"
                />
                <Button
                  variant="primary"
                  size="sm"
                  type="submit"
                  disabled={!timelineComment.trim() || noteMutation.isPending}
                  loading={noteMutation.isPending}
                  className="self-end"
                >
                  Post
                </Button>
              </form>
              <p className="text-[10px] text-text-tertiary">Only you and other staff can see comments.</p>
              <EventTimeline events={order.events} />
            </div>
          </Card>
        </div>

        {/* ═══ RIGHT SIDEBAR (Shopify order: Customer → Notes → Tags) ═══ */}
        <div className="space-y-4">
          {/* Customer */}
          <Card padding="none">
            <div className="flex items-start justify-between gap-2 px-4 pt-4 pb-2 sm:px-5">
              <h3 className="text-lg font-semibold text-text-primary">Customer</h3>
            </div>
            <div className="space-y-3 px-4 pb-4 sm:px-5 sm:pb-5">
              <div>
                <p className="text-sm font-medium">
                  <span className="text-brand-primary hover:underline cursor-pointer">
                    {order.customerName ?? '—'}
                  </span>
                </p>
                {order.customerOrderCount != null && order.customerOrderCount > 0 && (
                  <p className="text-xs mt-0.5">
                    <span className="text-brand-primary hover:underline cursor-pointer">
                      {order.customerOrderCount} order{order.customerOrderCount !== 1 ? 's' : ''}
                    </span>
                  </p>
                )}
                {order.organizationName && (
                  <p className="text-[11px] text-text-tertiary mt-0.5">{order.organizationName}</p>
                )}
              </div>
              <div className="space-y-1">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-text-tertiary">
                  Contact information
                </p>
                {order.customerEmail ? (
                  <p className="text-xs">
                    <a href={`mailto:${order.customerEmail}`} className="text-brand-primary hover:underline truncate block">
                      {order.customerEmail}
                    </a>
                  </p>
                ) : (
                  <p className="text-xs text-text-tertiary italic">No email</p>
                )}
                {(order.customerPhone || addr?.phone) ? (
                  <p className="text-xs text-text-secondary">{order.customerPhone || addr?.phone}</p>
                ) : (
                  <p className="text-xs text-text-tertiary italic">No phone number</p>
                )}
              </div>
              <div className="space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-text-tertiary">
                    {(sentFulfillmentType ?? checkoutMethod.type) === 'PICKUP'
                      ? 'Pickup location'
                      : 'Shipping address'}
                  </p>
                  {canManageOrder && !isProviderUser && !isTerminal && (
                    <button
                      type="button"
                      onClick={() => {
                        if (canEditAddress) {
                          openAddressEdit();
                        } else {
                          showFeedback(
                            'error',
                            `This order is in ${order.status.replace(/_/g, ' ').toLowerCase()} status — address changes are restricted.`,
                          );
                        }
                      }}
                      className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-brand-primary bg-brand-50 hover:bg-brand-100 transition-colors"
                    >
                      Edit
                    </button>
                  )}
                </div>
                {addr ? (
                  <div className="text-xs text-text-primary space-y-0.5 leading-relaxed">
                    {(addr.name || order.customerName) && <p>{addr.name ?? order.customerName}</p>}
                    {/* Shopify orders store street1/street2; portal orders address1/address2 */}
                    {(addr.street1 || addr.address1) && <p>{addr.street1 || addr.address1}</p>}
                    {(addr.street2 || addr.address2) && <p>{addr.street2 || addr.address2}</p>}
                    <p>{[addr.city, addr.province, addr.zip].filter(Boolean).join(', ')}</p>
                    {addr.country && <p>{addr.country}</p>}
                    {addr.phone && <p>{addr.phone}</p>}
                  </div>
                ) : (
                  <p className="text-xs text-text-tertiary italic">No shipping address provided</p>
                )}
                {(addr?.shipToSource === 'provider_warehouse' ||
                  addr?.shipToSource === 'tk_warehouse') && (
                  <p className="text-[11px] text-text-tertiary">
                    {SHIP_TO_SOURCE_LABEL[addr.shipToSource]} fallback. Edit this address to replace it with the customer ship-to.
                  </p>
                )}
                {order.shopifyDeliveryMethod === 'PICK_UP' && !addr && (
                  <p className="text-[11px] text-text-tertiary">
                    Local pickup has no customer ship-to. Sending as PICKUP uses the Shopify location, billing address, provider warehouse, or TK warehouse.
                  </p>
                )}
              </div>
              <div className="space-y-1">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-text-tertiary">
                  Billing address
                </p>
                {billingAddr ? (
                  <>
                    <div className="text-xs text-text-primary space-y-0.5 leading-relaxed">
                      {billingAddr.name && <p>{billingAddr.name}</p>}
                      {(billingAddr.street1 || billingAddr.address1) && <p>{billingAddr.street1 || billingAddr.address1}</p>}
                      {(billingAddr.street2 || billingAddr.address2) && <p>{billingAddr.street2 || billingAddr.address2}</p>}
                      <p>{[billingAddr.city, billingAddr.province, billingAddr.zip].filter(Boolean).join(', ')}</p>
                      {billingAddr.country && <p>{billingAddr.country}</p>}
                      {order.customerPhone && <p>{order.customerPhone}</p>}
                    </div>
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                        [billingAddr.street1 || billingAddr.address1, billingAddr.city, billingAddr.province, billingAddr.zip, billingAddr.country].filter(Boolean).join(', '),
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-brand-primary hover:underline mt-1 inline-block"
                    >
                      View map
                    </a>
                  </>
                ) : (
                  <p className="text-xs text-text-tertiary italic">No billing address provided</p>
                )}
              </div>
            </div>
          </Card>

          {/* Notes */}
          <Card>
            <CardHeader title="Notes" />
            <div className="space-y-2 px-4 pb-4 sm:px-6 sm:pb-5">
              <form
                onSubmit={(e: FormEvent) => {
                  e.preventDefault();
                  const val = noteInput.trim();
                  if (!val) return;
                  noteMutation.mutate(val);
                  setNoteInput('');
                }}
                className="space-y-1.5"
              >
                <textarea
                  placeholder="Leave a note..."
                  value={noteInput}
                  onChange={(e) => setNoteInput(e.target.value)}
                  onKeyDown={(e: KeyboardEvent<HTMLTextAreaElement>) => {
                    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                      e.preventDefault();
                      const val = noteInput.trim();
                      if (val) {
                        noteMutation.mutate(val);
                        setNoteInput('');
                      }
                    }
                  }}
                  rows={2}
                  className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-xs text-text-primary placeholder:text-text-tertiary focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary resize-none"
                />
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-text-tertiary">Ctrl+Enter</span>
                  <Button
                    variant="outline"
                    size="sm"
                    type="submit"
                    disabled={!noteInput.trim() || noteMutation.isPending}
                    loading={noteMutation.isPending}
                  >
                    Add
                  </Button>
                </div>
              </form>
              {(order.notes ?? []).length > 0 && (
                <div className="space-y-1.5 border-t border-border pt-2">
                  {(order.notes ?? []).map((note) => (
                    <div key={note.id} className="rounded-md bg-surface-secondary p-2 space-y-0.5">
                      <p className="text-xs text-text-primary whitespace-pre-wrap leading-relaxed">
                        {note.body}
                      </p>
                      <p className="text-[10px] text-text-tertiary">
                        {new Date(note.createdAt).toLocaleString('en-US', {
                          month: 'short', day: 'numeric', year: 'numeric',
                          hour: 'numeric', minute: '2-digit',
                        })}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>

          {/* Internal tags — admin-only labels, never synced to Shopify or
              visible to the merchant. Separate system from store tags below. */}
          <Card>
            <CardHeader title="Internal Tags" />
            <div className="space-y-2 px-4 pb-4 sm:px-6 sm:pb-5">
              {(order.internalTags ?? []).length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {(order.internalTags ?? []).map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-0.5 rounded-full border border-brand-200 bg-brand-50 px-2 py-0.5 text-[11px] text-brand-700"
                    >
                      {tag}
                      <button
                        onClick={() => removeTagMutation.mutate(tag)}
                        disabled={removeTagMutation.isPending}
                        className="ml-0.5 text-brand-400 hover:text-brand-700 transition-colors leading-none"
                        title={`Remove "${tag}"`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <form
                onSubmit={(e: FormEvent) => {
                  e.preventDefault();
                  const val = tagInput.trim();
                  if (!val) return;
                  addTagMutation.mutate(val);
                  setTagInput('');
                }}
                className="flex gap-1.5"
              >
                <Input
                  placeholder="Add internal tag..."
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  className="flex-1"
                />
                <Button
                  variant="outline"
                  size="sm"
                  type="submit"
                  disabled={!tagInput.trim() || addTagMutation.isPending}
                  loading={addTagMutation.isPending}
                >
                  Add
                </Button>
              </form>
              <p className="text-[11px] text-text-tertiary">
                Visible to admins only — never synced to Shopify or the portal.
              </p>
            </div>
          </Card>

          {/* Store tags — merchant-facing, two-way synced with Shopify for
              Shopify orders. Read-only here: the merchant (portal) or the
              store owns them. */}
          {(order.tags ?? []).length > 0 && (
            <Card>
              <CardHeader title="Store Tags" />
              <div className="space-y-2 px-4 pb-4 sm:px-6 sm:pb-5">
                <div className="flex flex-wrap gap-1">
                  {(order.tags ?? []).map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center rounded-full border border-border bg-surface-secondary px-2 py-0.5 text-[11px] text-text-secondary"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <p className="text-[11px] text-text-tertiary">
                  {order.source === 'SHOPIFY'
                    ? 'Synced two-way with the Shopify order — managed by the merchant or the store.'
                    : 'Managed by the merchant in the portal.'}
                </p>
              </div>
            </Card>
          )}
        </div>
      </div>

      <Modal
        open={!!labelModal}
        onClose={closeLabelModal}
        title={labelModal?.replacing ? 'Replace label' : 'Upload label'}
        description="The previous label file is kept. Carrier and tracking are saved with the new file."
        size="sm"
      >
        <div className="space-y-3">
          <Select
            label="Carrier"
            value={labelCarrier}
            onChange={(e) => setLabelCarrier(e.target.value)}
            options={CARRIERS}
          />
          {labelCarrier === 'Other' && (
            <Input
              label="Carrier name"
              value={labelCarrierOther}
              onChange={(e) => setLabelCarrierOther(e.target.value)}
              placeholder="Carrier name"
            />
          )}
          <div>
            <Input
              label="Tracking number"
              value={labelTrackingNumber}
              onChange={(e) => setLabelTrackingNumber(e.target.value)}
              placeholder="e.g. 1Z999AA10123456784"
            />
            {labelTrackingNumber && !isValidTrackingNumber(labelTrackingNumber) && (
              <p className="mt-1 text-[11px] text-red-500">
                Enter a valid tracking number (5-50 letters, digits, or dashes).
              </p>
            )}
          </div>
          {labelCarrier === 'Other' ? (
            <Input
              label="Tracking URL"
              value={labelCustomUrl}
              onChange={(e) => setLabelCustomUrl(e.target.value)}
              placeholder="https://..."
            />
          ) : (
            <Input
              label="Tracking URL (auto-generated)"
              value={
                labelCarrier && labelTrackingNumber
                  ? buildTrackingUrl(labelCarrier, labelTrackingNumber)
                  : ''
              }
              readOnly
              disabled
            />
          )}
          <label className="block text-xs font-medium text-text-primary">
            Label file (PDF or PNG, max 10 MB)
            <input
              type="file"
              accept="application/pdf,image/png"
              className="mt-1 block w-full text-xs text-text-secondary"
              onChange={(e) => setLabelFile(e.target.files?.[0] ?? null)}
            />
          </label>
          {labelFile && (
            <p className="text-[11px] text-text-tertiary">{labelFile.name}</p>
          )}
        </div>
        <ModalFooter>
          <Button variant="outline" onClick={closeLabelModal} disabled={uploadLabelMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={uploadLabelMutation.isPending}
            disabled={
              uploadLabelMutation.isPending ||
              !labelModal ||
              !labelFile ||
              !(labelCarrier === 'Other' ? labelCarrierOther.trim() : labelCarrier) ||
              !isValidTrackingNumber(labelTrackingNumber)
            }
            onClick={() => {
              if (!labelModal || !labelFile) return;
              if (!['application/pdf', 'image/png'].includes(labelFile.type)) {
                showFeedback('error', 'Only PDF or PNG label files are allowed.');
                return;
              }
              if (labelFile.size > 10 * 1024 * 1024) {
                showFeedback('error', 'Label exceeds the 10 MB size limit.');
                return;
              }
              const carrier =
                labelCarrier === 'Other' ? labelCarrierOther.trim() : labelCarrier;
              const trackingUrl =
                labelCarrier === 'Other'
                  ? labelCustomUrl.trim()
                  : buildTrackingUrl(carrier, labelTrackingNumber);
              uploadLabelMutation.mutate({
                shipmentId: labelModal.shipmentId,
                file: labelFile,
                carrier,
                trackingNumber: labelTrackingNumber.trim(),
                trackingUrl: trackingUrl || undefined,
              });
            }}
          >
            {labelModal?.replacing ? 'Replace label' : 'Upload label'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Danger action confirmation modal */}
      <Modal
        open={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        title="Confirm Action"
        description={confirmAction?.description ?? ''}
        size="sm"
      >
        <ModalFooter>
          <Button variant="outline" onClick={() => setConfirmAction(null)} disabled={isMutating}>
            Go Back
          </Button>
          <Button
            variant={confirmAction?.variant === 'warning' ? 'outline' : 'danger'}
            onClick={() => {
              confirmAction?.onConfirm();
            }}
            loading={isMutating}
            disabled={isMutating}
          >
            {confirmAction?.label ?? 'Confirm'}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        open={addressEditOpen}
        onClose={() => setAddressEditOpen(false)}
        title="Edit shipping address"
        size="md"
      >
        <div className="space-y-3">
          {addressMutation.isError && (
            <p className="text-xs text-rose-600">
              {extractErrorMessage(
                addressMutation.error instanceof Error
                  ? addressMutation.error
                  : new Error('Failed to update address.'),
                'Failed to update address.',
              )}
            </p>
          )}
          {order.shopifyDeliveryMethod === 'PICK_UP' && (
            <p className="text-xs text-text-secondary">
              Pickup orders often have no Shopify ship-to. Prefill from billing when present; Inkteo still needs a complete street address.
            </p>
          )}
          <Input
            label="Full Name"
            value={addressForm.name}
            onChange={(e) => setAddressForm((p) => ({ ...p, name: e.target.value }))}
            placeholder="John Doe"
          />
          <Input
            label="Address Line 1"
            value={addressForm.address1}
            onChange={(e) => setAddressForm((p) => ({ ...p, address1: e.target.value }))}
            placeholder="123 Main St"
          />
          <Input
            label="Address Line 2"
            value={addressForm.address2}
            onChange={(e) => setAddressForm((p) => ({ ...p, address2: e.target.value }))}
            placeholder="Suite 100"
          />
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="City"
              value={addressForm.city}
              onChange={(e) => setAddressForm((p) => ({ ...p, city: e.target.value }))}
              placeholder="Houston"
            />
            <Input
              label="State / Province"
              value={addressForm.province}
              onChange={(e) => setAddressForm((p) => ({ ...p, province: e.target.value }))}
              placeholder="TX"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="ZIP / Postal Code"
              value={addressForm.zip}
              onChange={(e) => setAddressForm((p) => ({ ...p, zip: e.target.value }))}
              placeholder="77001"
            />
            <Input
              label="Country"
              value={addressForm.country}
              onChange={(e) => setAddressForm((p) => ({ ...p, country: e.target.value }))}
              placeholder="US"
            />
          </div>
          <Input
            label="Phone"
            value={addressForm.phone}
            onChange={(e) => setAddressForm((p) => ({ ...p, phone: e.target.value }))}
            placeholder="+1 555-123-4567"
          />
        </div>
        <ModalFooter>
          <Button
            variant="outline"
            onClick={() => setAddressEditOpen(false)}
            disabled={addressMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAddressSave}
            loading={addressMutation.isPending}
            disabled={addressMutation.isPending || !addressForm.address1.trim()}
          >
            Save address
          </Button>
        </ModalFooter>
      </Modal>

      {/* Provider Reject / Problem modal */}
      <Modal
        open={showProviderRejectModal}
        onClose={() => setShowProviderRejectModal(false)}
        title={providerRejectAction === 'REJECT' ? 'Reject Order' : 'Report Problem'}
        size="sm"
      >
        <div className="space-y-3 p-4">
          <Select
            label="Reason"
            value={providerRejectReason}
            onChange={(e) => setProviderRejectReason(e.target.value)}
            options={[
              { value: '', label: 'Select reason...' },
              { value: 'out_of_stock', label: 'Out of Stock' },
              { value: 'machine_broken', label: 'Machine Broken' },
              { value: 'file_corrupt', label: 'File Corrupt' },
              { value: 'quality_issue', label: 'Quality Issue' },
              { value: 'other', label: 'Other' },
            ]}
          />
        </div>
        <ModalFooter>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowProviderRejectModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant={providerRejectAction === 'REJECT' ? 'danger' : 'outline'}
            size="sm"
            onClick={() =>
              providerActionMutation.mutate({
                action: providerRejectAction,
                reason: providerRejectReason,
              })
            }
            disabled={!providerRejectReason || providerActionMutation.isPending}
            loading={providerActionMutation.isPending}
          >
            {providerRejectAction === 'REJECT' ? 'Reject Order' : 'Report Problem'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Ship with Tracking modal — opens when user clicks "Mark as Shipped" without tracking */}
      <Modal
        open={showShipTrackingModal}
        onClose={() => setShowShipTrackingModal(false)}
        title="Add Tracking & Ship"
        size="sm"
      >
        <div className="space-y-3 p-4">
          <p className="text-xs text-text-secondary">
            A tracking number is required before marking this order as shipped.
            This will create a shipment for all unfulfilled items and mark the order as shipped.
          </p>
          <Select
            label="Carrier"
            value={shipCarrier}
            onChange={(e) => setShipCarrier(e.target.value)}
            options={CARRIERS}
          />
          <div>
            <Input
              label="Tracking Number"
              value={shipTrackingNumber}
              onChange={(e) => setShipTrackingNumber(e.target.value)}
              placeholder="e.g. 1Z999AA10123456784"
            />
            {shipTrackingNumber && !isValidTrackingNumber(shipTrackingNumber) && (
              <p className="mt-1 text-[11px] text-red-500">
                Please enter a valid tracking number (5-50 alphanumeric characters or dashes)
              </p>
            )}
          </div>
          {shipCarrier === 'Other' ? (
            <Input
              label="Tracking URL"
              value={shipCustomUrl}
              onChange={(e) => setShipCustomUrl(e.target.value)}
              placeholder="https://..."
            />
          ) : (
            <Input
              label="Tracking URL (auto-generated)"
              value={shipCarrier && shipTrackingNumber ? buildTrackingUrl(shipCarrier, shipTrackingNumber) : ''}
              readOnly
              disabled
            />
          )}
          {shipCarrier && shipTrackingNumber && isValidTrackingNumber(shipTrackingNumber) && (
            <p className="text-[11px] text-text-tertiary">
              {shipCarrier === 'Other'
                ? (shipCustomUrl ? `Preview: ${shipCustomUrl}` : 'Enter a custom tracking URL above')
                : `Preview: ${buildTrackingUrl(shipCarrier, shipTrackingNumber)}`}
            </p>
          )}
          {shipWithTrackingMutation.isError && (
            <p className="text-xs text-red-500">
              {extractErrorMessage(shipWithTrackingMutation.error as Error, 'Failed to ship order')}
            </p>
          )}
        </div>
        <ModalFooter>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setShowShipTrackingModal(false);
              setShipCarrier('');
              setShipTrackingNumber('');
              setShipCustomUrl('');
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              const trackingUrl = shipCustomUrl || buildTrackingUrl(shipCarrier, shipTrackingNumber);
              shipWithTrackingMutation.mutate({
                carrier: shipCarrier,
                trackingNumber: shipTrackingNumber,
                trackingUrl: trackingUrl || undefined,
              });
            }}
            disabled={!shipCarrier || !shipTrackingNumber || !isValidTrackingNumber(shipTrackingNumber) || shipWithTrackingMutation.isPending}
            loading={shipWithTrackingMutation.isPending}
          >
            Ship Order
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

const REFUND_REASON_OPTIONS = [
  { value: RefundReason.REQUESTED_BY_CUSTOMER, label: 'Requested by customer' },
  { value: RefundReason.DUPLICATE, label: 'Duplicate' },
  { value: RefundReason.FRAUDULENT, label: 'Fraudulent' },
  { value: RefundReason.PRODUCT_UNACCEPTABLE, label: 'Product unacceptable' },
  { value: RefundReason.ORDER_CHANGE, label: 'Order change' },
  { value: RefundReason.OTHER, label: 'Other' },
];

function dollarsToCents(input: string): bigint | null {
  const trimmed = input.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  const [whole, frac = ''] = trimmed.split('.');
  try {
    return BigInt(whole) * 100n + BigInt(frac.padEnd(2, '0'));
  } catch {
    return null;
  }
}

function WholesaleRefundSection({
  orderId,
  currency,
  settledVia,
  confirm,
}: {
  orderId: string;
  currency: string;
  settledVia: string | null;
  confirm: (opts?: ConfirmOptions) => Promise<boolean>;
}) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const stripeDisabled = settledVia === 'HOUSE';
  const [amount, setAmount] = useState('');
  const [mode, setMode] = useState<string>(RefundMode.WALLET_CREDIT);
  const [reason, setReason] = useState<string>(RefundReason.ORDER_CHANGE);
  const [note, setNote] = useState('');
  const attemptKeyRef = useRef<string | null>(null);

  const listQuery = useQuery<ListOrderRefundsResponse>({
    queryKey: ['admin', 'orders', orderId, 'refunds'],
    queryFn: () =>
      api.get<ListOrderRefundsResponse>(
        `/api/v1/admin/orders/${orderId}/refunds`,
      ),
  });

  const createMutation = useMutation({
    mutationFn: (body: {
      mode: string;
      amountCents: string;
      reason: string;
      reasonNote?: string;
      idempotencyKey: string;
    }) =>
      api.post<CreateOrderRefundResponse>(
        `/api/v1/admin/orders/${orderId}/refunds`,
        body,
      ),
    onSuccess: () => {
      attemptKeyRef.current = null;
      queryClient.invalidateQueries({
        queryKey: ['admin', 'orders', orderId],
      });
      setAmount('');
      setNote('');
    },
    onError: () => {
      // Fresh key on the next click so a FAILED row can be retried
      // (replays of the same key return the existing row as-is).
      attemptKeyRef.current = null;
    },
  });

  const remaining = listQuery.data?.remainingRefundableCents ?? '0';
  const remainingLabel = formatMoneyDto({
    amountCents: remaining,
    currency: (currency || 'USD') as 'USD',
  });

  async function submit() {
    const cents = dollarsToCents(amount);
    if (cents === null || cents <= 0n) return;
    const remainingBig = BigInt(remaining);
    if (cents > remainingBig) return;
    const resolvedMode = stripeDisabled
      ? RefundMode.WALLET_CREDIT
      : (mode as (typeof RefundMode)[keyof typeof RefundMode]);
    const destination = stripeDisabled
      ? 'house settlement unwind (organization wallet stays at zero)'
      : resolvedMode === RefundMode.STRIPE
        ? 'Stripe'
        : 'wallet credit';
    const ok = await confirm({
      title: 'Issue refund',
      description: `Refund ${formatMoneyDto({
        amountCents: cents.toString(),
        currency: (currency || 'USD') as 'USD',
      })} via ${destination}?`,
      confirmLabel: 'Refund',
      tone: 'danger',
    });
    if (!ok) return;
    if (!attemptKeyRef.current) {
      attemptKeyRef.current = `admin_refund_${orderId}_${crypto.randomUUID()}`;
    }
    createMutation.mutate({
      mode: resolvedMode,
      amountCents: cents.toString(),
      reason,
      reasonNote: note.trim() || undefined,
      idempotencyKey: attemptKeyRef.current,
    });
  }

  return (
    <div className="border-t border-border pt-3 mt-2 space-y-3">
      <p className="text-xs font-medium uppercase tracking-wide text-text-tertiary">
        Refund
      </p>
      {listQuery.isLoading ? (
        <Spinner size="sm" />
      ) : (
        <>
          <p className="text-xs text-text-secondary">
            Remaining refundable: {remainingLabel}
          </p>
          {listQuery.data?.refunds.length ? (
            <ul className="space-y-1">
              {listQuery.data.refunds.map((r) => (
                <li
                  key={r.id}
                  className="flex justify-between text-xs text-text-secondary"
                >
                  <span>
                    {r.mode} · {r.status} · {r.reason}
                  </span>
                  <span>{formatMoneyDto(r.amount)}</span>
                </li>
              ))}
            </ul>
          ) : null}
          {remaining !== '0' && (
            <div className="space-y-2">
              <Input
                label="Amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
              />
              <Select
                label="Destination"
                value={mode}
                onChange={(e) => setMode(e.target.value)}
                disabled={stripeDisabled}
                options={[
                  { value: RefundMode.WALLET_CREDIT, label: 'Wallet credit' },
                  {
                    value: RefundMode.STRIPE,
                    label: stripeDisabled
                      ? 'Stripe (not available for house accounts)'
                      : 'Stripe',
                    disabled: stripeDisabled,
                  },
                ]}
              />
              <Select
                label="Reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                options={REFUND_REASON_OPTIONS}
              />
              <Input
                label="Note (optional)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
              {createMutation.isError && (
                <p className="text-xs text-rose-600">
                  {createMutation.error instanceof Error
                    ? createMutation.error.message
                    : 'Refund failed'}
                </p>
              )}
              <div className="flex justify-end">
                <Button
                  size="sm"
                  variant="danger"
                  disabled={
                    createMutation.isPending ||
                    dollarsToCents(amount) === null ||
                    remaining === '0'
                  }
                  loading={createMutation.isPending}
                  onClick={() => void submit()}
                >
                  Issue refund
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
